const TAU = Math.PI * 2;

const DEFAULT_SCENES = {
  ghostline: {
    seed: 0x61a5d3,
    quality: { mode: "high", dprCap: 1.65, fps: 30, pedestrianCount: 28, detail: 1 },
    palette: {
      void: "#020508",
      black: "#05090d",
      gunmetal: "#151e25",
      steel: "#60717c",
      white: "#edfaff",
      cyan: "#37e8ff",
      blue: "#286dff",
      red: "#f42b35",
    },
    source: { enabled: false, opacity: 0.96, intensity: 1, speed: 1 },
    primaryEnvironment: { enabled: false, opacity: 1, depth: 1, speed: 0.28 },
    secondaryMotion: { enabled: false, opacity: 0.8, density: 1, speed: 1 },
    environmentalResponse: { enabled: true, opacity: 0.74, intensity: 1 },
    decayAtmosphere: { enabled: true, opacity: 0.58, density: 1, speed: 0.22 },
    legibility: { enabled: true, opacity: 0.7, x: 0.55, y: 0.08, width: 0.41, height: 0.84 },
    stillTime: 3.18,
  },
  "orbital-commons": {
    seed: 0x0b17a1,
    quality: { mode: "high", dprCap: 1.65, fps: 30, pedestrianCount: 16, detail: 1 },
    palette: {
      void: "#010408",
      black: "#05080c",
      gunmetal: "#182128",
      steel: "#7d909b",
      white: "#f4fbff",
      cyan: "#58eaff",
      blue: "#3b75ff",
      red: "#f22d38",
    },
    source: { enabled: false, opacity: 0.94, intensity: 1, speed: 0.6 },
    primaryEnvironment: { enabled: false, opacity: 1, depth: 1, speed: 0.12 },
    secondaryMotion: { enabled: false, opacity: 0.82, density: 1, speed: 0.72 },
    environmentalResponse: { enabled: true, opacity: 0.72, intensity: 1 },
    decayAtmosphere: { enabled: true, opacity: 0.52, density: 1, speed: 0.14 },
    legibility: { enabled: true, opacity: 0.72, x: 0.05, y: 0.08, width: 0.41, height: 0.84 },
    stillTime: 4.4,
  },
  "phase-foundry": {
    seed: 0xf04d12,
    quality: { mode: "high", dprCap: 1.65, fps: 30, pedestrianCount: 18, detail: 1 },
    palette: {
      void: "#030405",
      black: "#07090a",
      gunmetal: "#20252a",
      steel: "#9ca8ad",
      white: "#fff8ee",
      cyan: "#39dff0",
      blue: "#3975e8",
      red: "#f12f32",
      ember: "#ff9a3c",
    },
    source: { enabled: false, opacity: 0.98, intensity: 1, speed: 0.82 },
    primaryEnvironment: { enabled: false, opacity: 1, depth: 1, speed: 0.18 },
    secondaryMotion: { enabled: false, opacity: 0.83, density: 1, speed: 0.9 },
    environmentalResponse: { enabled: true, opacity: 0.8, intensity: 1 },
    decayAtmosphere: { enabled: true, opacity: 0.6, density: 1, speed: 0.2 },
    legibility: { enabled: true, opacity: 0.73, x: 0.54, y: 0.08, width: 0.41, height: 0.84 },
    stillTime: 2.7,
  },
};

function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

function mix(a, b, amount) {
  return a + (b - a) * amount;
}

function smoothstep(edge0, edge1, value) {
  const x = clamp((value - edge0) / Math.max(0.00001, edge1 - edge0));
  return x * x * (3 - 2 * x);
}

function hash(seed, index) {
  let value = (seed ^ Math.imul(index + 1, 0x45d9f3b)) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967296;
}

function deepMerge(base, override) {
  if (!override || typeof override !== "object" || Array.isArray(override)) return { ...base };
  const result = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = deepMerge(base?.[key] || {}, value);
    } else if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

function parseColor(color) {
  const value = String(color || "#ffffff").replace("#", "");
  if (value.length === 3) {
    return value.split("").map((part) => parseInt(part + part, 16));
  }
  return [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
}

function rgba(color, alpha) {
  const [red, green, blue] = parseColor(color);
  return `rgba(${red},${green},${blue},${clamp(alpha)})`;
}

function layerConfig(scene, names, fallback) {
  const sources = [];
  for (const name of names) {
    if (scene?.layers?.[name]) sources.push(scene.layers[name]);
    if (scene?.[name]) sources.push(scene[name]);
  }
  return sources.reduce((current, source) => deepMerge(current, source), fallback);
}

function resolveScene(input) {
  const supplied = typeof input === "object" && input ? input : {};
  const id = typeof input === "string"
    ? input
    : supplied.id || supplied.slug || supplied.concept || "ghostline";
  const canonical = id === "orbital" ? "orbital-commons" : id === "foundry" ? "phase-foundry" : id;
  const safeId = DEFAULT_SCENES[canonical] ? canonical : "ghostline";
  const scene = deepMerge(DEFAULT_SCENES[safeId], supplied.scene || {});
  if (typeof scene.quality === "string") scene.quality = { mode: scene.quality };
  scene.quality = deepMerge(DEFAULT_SCENES[safeId].quality, scene.quality || {});
  if (supplied.palette) {
    scene.palette = deepMerge(scene.palette, {
      void: supplied.palette.ink,
      black: supplied.palette.panel,
      white: supplied.palette.white,
      cyan: supplied.palette.accent || supplied.palette.cyan,
      blue: supplied.palette.secondary,
      red: supplied.palette.danger || supplied.palette.red,
      steel: supplied.palette.line,
    });
  }
  scene.source = layerConfig(scene, ["source", "core", "sourceCore"], DEFAULT_SCENES[safeId].source);
  scene.primaryEnvironment = layerConfig(
    scene,
    ["primaryEnvironment", "primary", "environment"],
    DEFAULT_SCENES[safeId].primaryEnvironment,
  );
  scene.secondaryMotion = layerConfig(
    scene,
    ["secondaryMotion", "secondary", "motion"],
    DEFAULT_SCENES[safeId].secondaryMotion,
  );
  scene.environmentalResponse = layerConfig(
    scene,
    ["environmentalResponse", "response", "lightingResponse"],
    DEFAULT_SCENES[safeId].environmentalResponse,
  );
  scene.decayAtmosphere = layerConfig(
    scene,
    ["decayAtmosphere", "decay", "atmosphere"],
    DEFAULT_SCENES[safeId].decayAtmosphere,
  );
  if (supplied.motion) {
    const activity = clamp(Number(supplied.motion.activity ?? 1), 0.08, 1.8);
    const density = clamp(Number(supplied.motion.density ?? 1), 0.25, 1.5);
    scene.motion = deepMerge(scene.motion || {}, supplied.motion);
    scene.source = deepMerge(scene.source, {
      intensity: clamp(Number(supplied.motion.bloom ?? scene.source.intensity), 0.1, 1.5),
      speed: scene.source.speed * activity,
    });
    scene.primaryEnvironment = deepMerge(scene.primaryEnvironment, {
      speed: scene.primaryEnvironment.speed * activity,
    });
    scene.secondaryMotion = deepMerge(scene.secondaryMotion, {
      density,
      speed: scene.secondaryMotion.speed * activity,
    });
    scene.decayAtmosphere = deepMerge(scene.decayAtmosphere, {
      density,
      speed: scene.decayAtmosphere.speed * activity,
    });
    if (Number.isFinite(Number(supplied.motion.pedestrians))) {
      scene.quality.pedestrianCount = Math.max(4, Number(supplied.motion.pedestrians));
    }
  }
  return { id: safeId, scene };
}

function queryRequestsStillFrame() {
  if (typeof window === "undefined") return false;
  try {
    const value = new URLSearchParams(window.location.search).get("still");
    return value !== null && !["0", "false", "off", "no"].includes(value.toLowerCase());
  } catch {
    return false;
  }
}

function queryCaptureTimeOverride() {
  if (typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get("time");
    if (value === null || value.trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? clamp(parsed, 0, 20) : null;
  } catch {
    return null;
  }
}
function roundedRectPath(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function makePopulation(seed, count, kind = "walker") {
  return Array.from({ length: count }, (_, index) => ({
    lane: mix(-0.92, 0.92, hash(seed, index * 11 + 1)),
    base: hash(seed, index * 11 + 2),
    speed: mix(0.025, 0.07, hash(seed, index * 11 + 3)),
    height: mix(0.76, 1.18, hash(seed, index * 11 + 4)),
    phase: hash(seed, index * 11 + 5) * TAU,
    facing: hash(seed, index * 11 + 6) > 0.5 ? 1 : -1,
    tone: hash(seed, index * 11 + 7),
    kind,
  }));
}

function makeSceneData(id, scene, qualityScale) {
  const seed = Number(scene.seed) || DEFAULT_SCENES[id].seed;
  const pedestrianCount = Math.max(6, Math.round((scene.quality.pedestrianCount || 18) * qualityScale));
  return {
    stars: Array.from({ length: Math.round(190 * qualityScale) }, (_, index) => ({
      x: hash(seed, index * 5 + 3),
      y: hash(seed, index * 5 + 4) * 0.76,
      size: mix(0.35, 1.8, hash(seed, index * 5 + 5)),
      phase: hash(seed, index * 5 + 6) * TAU,
      depth: mix(0.2, 1, hash(seed, index * 5 + 7)),
    })),
    buildings: Array.from({ length: Math.round(42 * qualityScale) }, (_, index) => ({
      x: hash(seed, index * 7 + 41),
      width: mix(0.018, 0.055, hash(seed, index * 7 + 42)),
      height: mix(0.08, 0.34, hash(seed, index * 7 + 43)),
      tiers: 1 + Math.floor(hash(seed, index * 7 + 44) * 4),
      windows: 2 + Math.floor(hash(seed, index * 7 + 45) * 5),
      accent: hash(seed, index * 7 + 46),
    })),
    walkers: makePopulation(seed ^ 0x37af22, pedestrianCount),
    workers: makePopulation(seed ^ 0x0ac12e, Math.max(8, Math.round(pedestrianCount * 0.72)), "worker"),
    dataDrops: Array.from({ length: Math.round(52 * qualityScale) }, (_, index) => ({
      x: hash(seed, index * 7 + 301),
      y: hash(seed, index * 7 + 302),
      speed: mix(0.025, 0.11, hash(seed, index * 7 + 303)),
      length: mix(0.018, 0.085, hash(seed, index * 7 + 304)),
      depth: mix(0.18, 1, hash(seed, index * 7 + 305)),
      phase: hash(seed, index * 7 + 306) * TAU,
    })),
    dust: Array.from({ length: Math.round(88 * qualityScale) }, (_, index) => ({
      x: hash(seed, index * 6 + 511),
      y: hash(seed, index * 6 + 512),
      speed: mix(0.003, 0.018, hash(seed, index * 6 + 513)),
      depth: mix(0.15, 1, hash(seed, index * 6 + 514)),
      phase: hash(seed, index * 6 + 515) * TAU,
      drift: mix(-1, 1, hash(seed, index * 6 + 516)),
    })),
    shuttles: Array.from({ length: Math.round(6 * qualityScale) }, (_, index) => ({
      base: hash(seed, index * 8 + 701),
      speed: mix(0.018, 0.055, hash(seed, index * 8 + 702)),
      lane: mix(-0.72, 0.72, hash(seed, index * 8 + 703)),
      elevation: mix(0.12, 0.5, hash(seed, index * 8 + 704)),
      depth: mix(0.35, 1, hash(seed, index * 8 + 705)),
    })),
    sparks: Array.from({ length: Math.round(96 * qualityScale) }, (_, index) => ({
      phase: hash(seed, index * 8 + 901),
      life: mix(0.6, 1.65, hash(seed, index * 8 + 902)),
      vx: mix(-0.15, 0.18, hash(seed, index * 8 + 903)),
      vy: mix(-0.55, -0.1, hash(seed, index * 8 + 904)),
      size: mix(0.45, 1.8, hash(seed, index * 8 + 905)),
      depth: mix(0.25, 1, hash(seed, index * 8 + 906)),
    })),
  };
}

function generatedEntityCount(data) {
  return Object.values(data).reduce(
    (total, collection) => total + (Array.isArray(collection) ? collection.length : 0),
    0,
  );
}

function renderedEntityBreakdown(id, data, scene) {
  const primaryEnabled = scene.primaryEnvironment?.enabled !== false;
  const secondaryEnabled = scene.secondaryMotion?.enabled !== false;
  const atmosphereEnabled = scene.decayAtmosphere?.enabled !== false;
  const proceduralPeopleEnabled = secondaryEnabled && scene.motion?.proceduralPeople !== false;
  const people = proceduralPeopleEnabled
    ? (id === "phase-foundry" ? data.workers.length : data.walkers.length)
    : 0;
  const architecture = primaryEnabled && id === "ghostline" ? data.buildings.length : 0;
  const stars = primaryEnabled && id !== "phase-foundry" ? data.stars.length : 0;
  let secondaryMotion = 0;
  if (secondaryEnabled) {
    if (id === "ghostline") secondaryMotion = data.dataDrops.length;
    else if (id === "orbital-commons") secondaryMotion = data.shuttles.length;
    else secondaryMotion = data.sparks.length;
  }
  const atmosphere = atmosphereEnabled ? data.dust.length : 0;
  return {
    people,
    architecture,
    stars,
    secondaryMotion,
    atmosphere,
    total: people + architecture + stars + secondaryMotion + atmosphere,
  };
}

function drawStarfield(ctx, width, height, data, palette, time, parallax, opacity = 1) {
  for (const star of data.stars) {
    const twinkle = 0.58 + 0.42 * Math.sin(time * (0.45 + star.depth) + star.phase);
    const x = ((star.x + parallax.x * 0.004 * star.depth + 1) % 1) * width;
    const y = star.y * height + parallax.y * 3 * star.depth;
    ctx.fillStyle = rgba(star.depth > 0.76 ? palette.cyan : palette.white, opacity * twinkle * star.depth * 0.55);
    ctx.fillRect(x, y, star.size * star.depth, star.size * star.depth);
  }
}

function drawPerson(ctx, x, footY, stature, phase, facing, palette, opacity, mode = "walk") {
  if (stature < 4) return;
  const walk = mode === "walk" ? Math.sin(phase) : Math.sin(phase * 0.37) * 0.32;
  const counter = -walk;
  const gesture = mode === "gesture" ? 0.55 + 0.35 * Math.sin(phase * 0.61) : 0;
  const bob = mode === "walk" ? Math.abs(Math.cos(phase)) * stature * 0.012 : Math.sin(phase * 0.28) * stature * 0.008;
  const headY = footY - stature + bob;
  const shoulderY = headY + stature * 0.24;
  const hipY = headY + stature * 0.57;
  const joint = Math.max(0.7, stature * 0.035);
  const near = opacity;
  const far = opacity * 0.52;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const shadow = ctx.createRadialGradient(x, footY + 1, 0, x, footY + 1, stature * 0.28);
  shadow.addColorStop(0, rgba(palette.cyan, near * 0.15));
  shadow.addColorStop(1, rgba(palette.black, 0));
  ctx.fillStyle = shadow;
  ctx.beginPath();
  ctx.ellipse(x, footY + 1, stature * 0.28, stature * 0.045, 0, 0, TAU);
  ctx.fill();

  const legLength = stature * 0.4;
  const hipSpread = stature * 0.06;
  for (const side of [-1, 1]) {
    const swing = walk * side;
    const hipX = x + side * hipSpread;
    const kneeX = hipX + swing * stature * 0.09;
    const kneeY = hipY + legLength * 0.48 - Math.max(0, -swing) * stature * 0.04;
    const footX = hipX + swing * stature * 0.16;
    ctx.strokeStyle = rgba(side === facing ? palette.steel : palette.gunmetal, side === facing ? near : far);
    ctx.lineWidth = joint * (side === facing ? 1.1 : 0.85);
    ctx.beginPath();
    ctx.moveTo(hipX, hipY);
    ctx.lineTo(kneeX, kneeY);
    ctx.lineTo(footX, footY);
    ctx.lineTo(footX + facing * stature * 0.065, footY);
    ctx.stroke();
  }

  const bodyGradient = ctx.createLinearGradient(x - stature * 0.1, 0, x + stature * 0.12, 0);
  bodyGradient.addColorStop(0, rgba(palette.gunmetal, near));
  bodyGradient.addColorStop(0.58, rgba(palette.steel, near * 0.76));
  bodyGradient.addColorStop(1, rgba(palette.black, near));
  ctx.fillStyle = bodyGradient;
  ctx.beginPath();
  ctx.moveTo(x - stature * 0.12, shoulderY);
  ctx.lineTo(x + stature * 0.12, shoulderY);
  ctx.lineTo(x + stature * 0.09, hipY);
  ctx.lineTo(x - stature * 0.08, hipY);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(palette.white, near * 0.14);
  ctx.lineWidth = Math.max(0.45, stature * 0.009);
  ctx.stroke();

  for (const side of [-1, 1]) {
    const armSwing = counter * side;
    const shoulderX = x + side * stature * 0.11;
    let elbowX = shoulderX + armSwing * stature * 0.105;
    let elbowY = shoulderY + stature * 0.2;
    let handX = shoulderX + armSwing * stature * 0.15;
    let handY = shoulderY + stature * 0.39;
    if (mode === "gesture" && side === facing) {
      elbowX += facing * stature * (0.09 + gesture * 0.08);
      elbowY -= stature * gesture * 0.04;
      handX += facing * stature * (0.16 + gesture * 0.06);
      handY -= stature * (0.12 + gesture * 0.12);
    }
    ctx.strokeStyle = rgba(side === facing ? palette.steel : palette.gunmetal, side === facing ? near : far);
    ctx.lineWidth = joint * (side === facing ? 1.05 : 0.82);
    ctx.beginPath();
    ctx.moveTo(shoulderX, shoulderY);
    ctx.lineTo(elbowX, elbowY);
    ctx.lineTo(handX, handY);
    ctx.stroke();
  }

  ctx.fillStyle = rgba(palette.gunmetal, near);
  ctx.beginPath();
  ctx.arc(x, headY + stature * 0.095, stature * 0.085, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(palette.cyan, near * 0.5);
  ctx.lineWidth = Math.max(0.45, stature * 0.009);
  ctx.beginPath();
  ctx.moveTo(x + facing * stature * 0.02, headY + stature * 0.087);
  ctx.lineTo(x + facing * stature * 0.078, headY + stature * 0.095);
  ctx.stroke();
  ctx.restore();
}

function drawPanel(ctx, x, y, width, height, palette, accent, opacity = 1) {
  const panel = ctx.createLinearGradient(x, y, x + width, y + height);
  panel.addColorStop(0, rgba(palette.black, opacity * 0.98));
  panel.addColorStop(0.46, rgba(palette.gunmetal, opacity * 0.96));
  panel.addColorStop(0.54, rgba(palette.steel, opacity * 0.42));
  panel.addColorStop(1, rgba(palette.black, opacity));
  ctx.fillStyle = panel;
  roundedRectPath(ctx, x, y, width, height, Math.min(width, height) * 0.08);
  ctx.fill();
  ctx.strokeStyle = rgba(accent, opacity * 0.48);
  ctx.lineWidth = Math.max(0.7, Math.min(width, height) * 0.018);
  ctx.stroke();
  const bolt = Math.max(0.75, Math.min(width, height) * 0.025);
  ctx.fillStyle = rgba(palette.white, opacity * 0.34);
  for (const [bx, by] of [[x + bolt * 2, y + bolt * 2], [x + width - bolt * 2, y + bolt * 2], [x + bolt * 2, y + height - bolt * 2], [x + width - bolt * 2, y + height - bolt * 2]]) {
    ctx.beginPath();
    ctx.arc(bx, by, bolt, 0, TAU);
    ctx.fill();
  }
}

function drawBeam(ctx, x1, y1, x2, y2, thickness, palette, accent, opacity = 1) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy);
  if (length < 1) return;
  ctx.save();
  ctx.translate(x1, y1);
  ctx.rotate(Math.atan2(dy, dx));
  const beam = ctx.createLinearGradient(0, -thickness / 2, 0, thickness / 2);
  beam.addColorStop(0, rgba(palette.black, opacity));
  beam.addColorStop(0.3, rgba(palette.steel, opacity * 0.6));
  beam.addColorStop(0.55, rgba(palette.gunmetal, opacity));
  beam.addColorStop(1, rgba(palette.black, opacity));
  ctx.fillStyle = beam;
  ctx.fillRect(0, -thickness / 2, length, thickness);
  ctx.strokeStyle = rgba(accent, opacity * 0.38);
  ctx.lineWidth = Math.max(0.65, thickness * 0.06);
  ctx.strokeRect(0, -thickness / 2, length, thickness);
  ctx.strokeStyle = rgba(palette.white, opacity * 0.1);
  ctx.beginPath();
  ctx.moveTo(0, -thickness * 0.16);
  ctx.lineTo(length, -thickness * 0.16);
  ctx.stroke();
  const bay = Math.max(thickness * 2.5, length / 8);
  for (let x = bay; x < length; x += bay) {
    ctx.fillStyle = rgba(palette.black, opacity * 0.9);
    ctx.fillRect(x - thickness * 0.08, -thickness * 0.56, thickness * 0.16, thickness * 1.12);
    ctx.fillStyle = rgba(palette.white, opacity * 0.26);
    ctx.beginPath();
    ctx.arc(x, 0, Math.max(0.7, thickness * 0.075), 0, TAU);
    ctx.fill();
  }
  ctx.restore();
}

function drawMagRailTrain(ctx, width, height, x, y, palette, opacity, cueStrength) {
  const totalWidth = width * 0.48;
  const carWidth = totalWidth / 5.2;
  const carHeight = height * 0.061;
  const trail = ctx.createLinearGradient(x - totalWidth * 0.55, 0, x + totalWidth, 0);
  trail.addColorStop(0, rgba(palette.cyan, 0));
  trail.addColorStop(0.7, rgba(palette.cyan, opacity * 0.13));
  trail.addColorStop(1, rgba(palette.white, opacity * 0.55));
  ctx.strokeStyle = trail;
  ctx.lineWidth = height * 0.006;
  ctx.beginPath();
  ctx.moveTo(x - totalWidth * 0.55, y + carHeight * 0.42);
  ctx.lineTo(x + totalWidth, y + carHeight * 0.42);
  ctx.stroke();
  for (let car = 0; car < 5; car += 1) {
    const carX = x + car * carWidth;
    const nose = car === 4;
    const hull = ctx.createLinearGradient(carX, y - carHeight / 2, carX, y + carHeight / 2);
    hull.addColorStop(0, rgba(palette.steel, opacity * 0.82));
    hull.addColorStop(0.18, rgba(palette.gunmetal, opacity));
    hull.addColorStop(0.72, rgba(palette.black, opacity));
    hull.addColorStop(1, rgba(palette.steel, opacity * 0.52));
    ctx.fillStyle = hull;
    ctx.beginPath();
    ctx.moveTo(carX + carWidth * 0.07, y - carHeight * 0.46);
    ctx.lineTo(carX + carWidth * (nose ? 0.82 : 0.94), y - carHeight * (nose ? 0.28 : 0.46));
    ctx.lineTo(carX + carWidth * (nose ? 0.98 : 0.94), y + carHeight * 0.2);
    ctx.lineTo(carX + carWidth * 0.84, y + carHeight * 0.45);
    ctx.lineTo(carX + carWidth * 0.07, y + carHeight * 0.45);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(car === 0 ? palette.red : palette.cyan, opacity * 0.56);
    ctx.lineWidth = 1.15;
    ctx.stroke();
    for (let windowIndex = 0; windowIndex < 4; windowIndex += 1) {
      const wx = carX + carWidth * (0.15 + windowIndex * 0.17);
      const glass = ctx.createLinearGradient(wx, y - carHeight * 0.23, wx, y + carHeight * 0.04);
      glass.addColorStop(0, rgba(palette.white, 0.68 + cueStrength * 0.16));
      glass.addColorStop(1, rgba(palette.cyan, 0.34));
      ctx.fillStyle = glass;
      ctx.fillRect(wx, y - carHeight * 0.23, carWidth * 0.105, carHeight * 0.24);
    }
    ctx.fillStyle = rgba(nose ? palette.white : palette.cyan, nose ? 0.88 : 0.3);
    ctx.fillRect(carX + carWidth * 0.12, y + carHeight * 0.26, carWidth * 0.68, Math.max(1, height * 0.0025));
  }
}

function drawOrbitalModule(ctx, x, y, width, height, rotation, palette, accent, opacity = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  drawPanel(ctx, -width / 2, -height / 2, width, height, palette, accent, opacity);
  ctx.fillStyle = rgba(palette.cyan, opacity * 0.38);
  for (let index = -2; index <= 2; index += 1) {
    ctx.fillRect(index * width * 0.13 - width * 0.035, -height * 0.12, width * 0.07, height * 0.23);
  }
  ctx.fillStyle = rgba(palette.white, opacity * 0.17);
  ctx.fillRect(-width * 0.34, -height * 0.34, width * 0.68, height * 0.05);
  ctx.restore();
}

function drawRobotArm(ctx, baseX, baseY, scale, time, phase, palette, opacity, facing = 1) {
  const shoulderAngle = facing * (-0.72 + Math.sin(time * 0.37 + phase) * 0.22);
  const elbowAngle = facing * (0.86 + Math.sin(time * 0.53 + phase * 1.4) * 0.3);
  const upper = scale * 0.72;
  const lower = scale * 0.58;
  const shoulderX = baseX;
  const shoulderY = baseY - scale * 0.13;
  const elbowX = shoulderX + Math.cos(shoulderAngle) * upper;
  const elbowY = shoulderY + Math.sin(shoulderAngle) * upper;
  const wristAngle = shoulderAngle + elbowAngle;
  const wristX = elbowX + Math.cos(wristAngle) * lower;
  const wristY = elbowY + Math.sin(wristAngle) * lower;
  drawPanel(ctx, baseX - scale * 0.22, baseY - scale * 0.2, scale * 0.44, scale * 0.4, palette, palette.red, opacity);
  drawBeam(ctx, shoulderX, shoulderY, elbowX, elbowY, scale * 0.18, palette, palette.cyan, opacity);
  drawBeam(ctx, elbowX, elbowY, wristX, wristY, scale * 0.14, palette, palette.red, opacity);
  for (const [x, y, accent] of [[shoulderX, shoulderY, palette.cyan], [elbowX, elbowY, palette.red], [wristX, wristY, palette.white]]) {
    const joint = ctx.createRadialGradient(x - scale * 0.025, y - scale * 0.025, scale * 0.01, x, y, scale * 0.15);
    joint.addColorStop(0, rgba(palette.white, opacity * 0.72));
    joint.addColorStop(0.35, rgba(accent, opacity * 0.54));
    joint.addColorStop(1, rgba(palette.black, opacity));
    ctx.fillStyle = joint;
    ctx.beginPath();
    ctx.arc(x, y, scale * 0.15, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(accent, opacity * 0.48);
    ctx.stroke();
  }
  ctx.save();
  ctx.translate(wristX, wristY);
  ctx.rotate(wristAngle);
  ctx.strokeStyle = rgba(palette.steel, opacity);
  ctx.lineWidth = scale * 0.055;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(scale * 0.24, -scale * 0.13);
  ctx.moveTo(0, 0);
  ctx.lineTo(scale * 0.24, scale * 0.13);
  ctx.stroke();
  ctx.restore();
}
function drawGhostlineStatic(ctx, width, height, data, scene) {
  const palette = scene.palette;
  const primary = scene.primaryEnvironment;
  const horizon = height * 0.48;
  const sky = ctx.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, palette.void);
  sky.addColorStop(0.5, "#07111a");
  sky.addColorStop(1, palette.black);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, height);
  if (!primary.enabled) return;
  ctx.save();
  ctx.globalAlpha = clamp(Number(primary.opacity ?? 1));
  drawStarfield(ctx, width, height, data, palette, scene.stillTime, { x: 0, y: 0 }, 0.22);

  for (const building of data.buildings) {
    const x = building.x * width;
    const buildingWidth = building.width * width;
    const buildingHeight = building.height * height;
    const baseY = horizon + height * 0.05;
    const gradient = ctx.createLinearGradient(x, 0, x + buildingWidth, 0);
    gradient.addColorStop(0, "#090d11");
    gradient.addColorStop(0.6, "#182029");
    gradient.addColorStop(1, "#05070a");
    ctx.fillStyle = gradient;
    ctx.fillRect(x - buildingWidth / 2, baseY - buildingHeight, buildingWidth, buildingHeight);
    ctx.strokeStyle = rgba(building.accent > 0.84 ? palette.red : palette.cyan, 0.1 + building.accent * 0.14);
    ctx.lineWidth = 0.7;
    ctx.strokeRect(x - buildingWidth / 2, baseY - buildingHeight, buildingWidth, buildingHeight);
    for (let row = 0; row < building.tiers + 2; row += 1) {
      for (let column = 0; column < building.windows; column += 1) {
        if (hash(scene.seed + row, column + Math.round(building.x * 10000)) > 0.54) continue;
        const wx = x - buildingWidth * 0.38 + (column / Math.max(1, building.windows - 1)) * buildingWidth * 0.76;
        const wy = baseY - buildingHeight + buildingHeight * 0.14 + row * buildingHeight * 0.16;
        ctx.fillStyle = rgba(building.accent > 0.9 ? palette.red : palette.cyan, 0.16 + building.accent * 0.25);
        ctx.fillRect(wx, wy, Math.max(0.55, buildingWidth * 0.045), Math.max(0.7, buildingHeight * 0.018));
      }
    }
  }

  const archXs = [0.04, 0.2, 0.39, 0.61, 0.81, 0.96];
  for (let index = 0; index < archXs.length; index += 1) {
    const x = width * archXs[index];
    const columnWidth = Math.max(11, width * 0.014);
    const columnTop = height * (0.13 + (index % 2) * 0.035);
    const column = ctx.createLinearGradient(x - columnWidth / 2, 0, x + columnWidth / 2, 0);
    column.addColorStop(0, rgba(palette.black, 0.98));
    column.addColorStop(0.48, rgba(palette.steel, 0.5));
    column.addColorStop(1, rgba(palette.black, 0.98));
    ctx.fillStyle = column;
    ctx.beginPath();
    ctx.moveTo(x - columnWidth * 0.72, horizon + height * 0.22);
    ctx.lineTo(x - columnWidth * 0.42, columnTop);
    ctx.lineTo(x + columnWidth * 0.42, columnTop);
    ctx.lineTo(x + columnWidth * 0.72, horizon + height * 0.22);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(index % 3 === 0 ? palette.red : palette.cyan, 0.28);
    ctx.lineWidth = 1;
    ctx.stroke();
    if (index < archXs.length - 1) {
      const nextX = width * archXs[index + 1];
      drawBeam(ctx, x, columnTop, nextX, height * (0.1 + ((index + 1) % 2) * 0.03), Math.max(7, height * 0.009), palette, index % 2 ? palette.cyan : palette.steel, 0.7);
    }
  }
  const ground = ctx.createLinearGradient(0, horizon, 0, height);
  ground.addColorStop(0, "#101920");
  ground.addColorStop(0.38, "#070b0e");
  ground.addColorStop(1, "#020304");
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, width, height - horizon);
  const vanishingX = width * 0.47;
  ctx.strokeStyle = rgba(palette.steel, 0.12);
  ctx.lineWidth = 1;
  for (let lane = -8; lane <= 8; lane += 1) {
    ctx.beginPath();
    ctx.moveTo(vanishingX + lane * 2, horizon);
    ctx.lineTo(width * 0.5 + lane * width * 0.085, height);
    ctx.stroke();
  }
  for (let row = 0; row < 15; row += 1) {
    const progress = Math.pow(row / 14, 2.05);
    const y = horizon + progress * (height - horizon);
    ctx.strokeStyle = rgba(palette.cyan, 0.035 + progress * 0.085);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  const trackY = horizon + height * 0.13;
  ctx.strokeStyle = rgba(palette.white, 0.2);
  ctx.lineWidth = Math.max(1, height * 0.0022);
  for (const offset of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(0, trackY + offset * 8);
    ctx.quadraticCurveTo(width * 0.45, trackY - 18, width, trackY + offset * 21);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(palette.gunmetal, 0.88);
  ctx.fillRect(0, trackY + 12, width, Math.max(3, height * 0.008));
  ctx.restore();
}

function drawGhostline(ctx, width, height, data, scene, time, parallax, cueStrength) {
  const palette = scene.palette;
  const source = scene.source;
  const primary = scene.primaryEnvironment;
  const secondary = scene.secondaryMotion;
  const response = scene.environmentalResponse;
  const atmosphere = scene.decayAtmosphere;
  const horizon = height * 0.48;
  if (primary.enabled) {
    const magrailCycle = (time * primary.speed * 0.095 + 0.12) % 1;
    const trainX = mix(-width * 0.58, width * 1.08, magrailCycle);
    const trainY = horizon + height * 0.072 + Math.sin(magrailCycle * Math.PI) * -height * 0.012;
    ctx.save();
    ctx.translate(parallax.x * -7, parallax.y * -2);
    drawMagRailTrain(ctx, width, height, trainX, trainY, palette, primary.opacity, cueStrength);
    ctx.restore();
  }

  if (source.enabled) {
    const coreX = width * 0.47 + parallax.x * -4;
    const coreY = horizon + height * 0.07 + parallax.y * -2;
    const pulse = 0.82 + 0.18 * Math.sin(time * source.speed * 2.7);
    const glow = ctx.createRadialGradient(coreX, coreY, 0, coreX, coreY, height * 0.12);
    glow.addColorStop(0, rgba(palette.white, source.opacity * 0.66 * pulse));
    glow.addColorStop(0.08, rgba(palette.cyan, source.opacity * 0.45));
    glow.addColorStop(0.38, rgba(palette.blue, source.opacity * 0.12));
    glow.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(coreX - height * 0.12, coreY - height * 0.12, height * 0.24, height * 0.24);
    ctx.strokeStyle = rgba(palette.white, source.opacity * (0.62 + cueStrength * 0.3));
    ctx.lineWidth = 1 + cueStrength * 1.4;
    ctx.beginPath();
    ctx.moveTo(coreX - width * 0.14, coreY + 2);
    ctx.quadraticCurveTo(coreX, coreY - height * 0.02, coreX + width * 0.2, coreY + 5);
    ctx.stroke();
  }

  if (secondary.enabled) {
    if (scene.motion?.proceduralPeople !== false) for (let index = 0; index < data.walkers.length; index += 1) {
      const person = data.walkers[index];
      const travel = (person.base + time * person.speed * secondary.speed * 0.72) % 1;
      const progress = 0.26 + travel * 0.74;
      const depth = Math.pow(progress, 1.42);
      const footY = horizon + height * (0.13 + depth * 0.43);
      const x = mix(width * 0.47, width * (0.5 + person.lane * 0.5), depth) + parallax.x * depth * 7;
      const stature = height * (0.032 + depth * 0.088) * person.height;
      const stridePhase = person.phase + travel * TAU * (4.2 + person.speed * 18);
      drawPerson(ctx, x, footY, stature, stridePhase, person.facing, palette, secondary.opacity * (0.66 + depth * 0.3), index % 7 === 0 ? "gesture" : "walk");
    }

    for (const drop of data.dataDrops) {
      const y = ((drop.y + time * drop.speed * secondary.speed + 1) % 1) * height;
      const x = drop.x * width + parallax.x * 5 * drop.depth;
      const length = drop.length * height;
      const alpha = secondary.opacity * drop.depth * (0.08 + 0.12 * Math.sin(time * 0.8 + drop.phase) ** 2);
      const gradient = ctx.createLinearGradient(x, y - length, x, y);
      gradient.addColorStop(0, rgba(palette.cyan, 0));
      gradient.addColorStop(0.72, rgba(drop.depth > 0.8 ? palette.white : palette.blue, alpha));
      gradient.addColorStop(1, rgba(palette.cyan, alpha * 0.08));
      ctx.strokeStyle = gradient;
      ctx.lineWidth = Math.max(0.45, drop.depth * 1.15);
      ctx.beginPath();
      ctx.moveTo(x, y - length);
      ctx.lineTo(x, y);
      ctx.stroke();
      if (drop.depth > 0.56) {
        ctx.fillStyle = rgba(palette.cyan, alpha * 0.65);
        ctx.fillRect(x - 2, y - length * 0.28, 4 + drop.depth * 3, 1);
      }
    }
  }

  if (response.enabled) {
    const scanY = horizon + ((time * 0.055) % 1) * (height - horizon);
    const reflection = ctx.createLinearGradient(0, scanY - 30, 0, scanY + 50);
    reflection.addColorStop(0, rgba(palette.cyan, 0));
    reflection.addColorStop(0.5, rgba(palette.cyan, response.opacity * (0.025 + cueStrength * 0.07)));
    reflection.addColorStop(1, rgba(palette.blue, 0));
    ctx.fillStyle = reflection;
    ctx.fillRect(0, scanY - 30, width, 80);
  }

  if (atmosphere.enabled) drawAtmosphere(ctx, width, height, data, scene, time, parallax, "cyan");
}

function drawOrbitalStatic(ctx, width, height, data, scene) {
  const palette = scene.palette;
  const primary = scene.primaryEnvironment;
  const backdrop = ctx.createLinearGradient(0, 0, width, height);
  backdrop.addColorStop(0, "#02050a");
  backdrop.addColorStop(0.52, "#060b13");
  backdrop.addColorStop(1, "#020305");
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, width, height);
  if (!primary.enabled) return;
  ctx.save();
  ctx.globalAlpha = clamp(Number(primary.opacity ?? 1));
  drawStarfield(ctx, width, height, data, palette, scene.stillTime, { x: 0, y: 0 }, 0.84);

  const planetX = width * 0.84;
  const planetY = height * 1.04;
  const radius = Math.max(width, height) * 0.63;
  const planet = ctx.createRadialGradient(planetX - radius * 0.28, planetY - radius * 0.32, radius * 0.08, planetX, planetY, radius);
  planet.addColorStop(0, "#253d53");
  planet.addColorStop(0.34, "#132334");
  planet.addColorStop(0.7, "#071019");
  planet.addColorStop(1, "#010204");
  ctx.fillStyle = planet;
  ctx.beginPath();
  ctx.arc(planetX, planetY, radius, Math.PI, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(palette.cyan, 0.24);
  ctx.lineWidth = Math.max(2, height * 0.005);
  ctx.beginPath();
  ctx.arc(planetX, planetY, radius, Math.PI * 1.08, Math.PI * 1.78);
  ctx.stroke();
  ctx.strokeStyle = rgba(palette.white, 0.08);
  ctx.lineWidth = Math.max(8, height * 0.018);
  ctx.beginPath();
  ctx.arc(planetX, planetY, radius * 0.988, Math.PI * 1.08, Math.PI * 1.74);
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.arc(planetX, planetY, radius * 0.99, 0, TAU);
  ctx.clip();
  for (let band = 0; band < 9; band += 1) {
    const bandY = planetY - radius * (0.76 - band * 0.13);
    ctx.strokeStyle = rgba(band % 3 === 0 ? palette.cyan : palette.white, 0.025 + (band % 4) * 0.014);
    ctx.lineWidth = radius * (0.012 + (band % 2) * 0.008);
    ctx.beginPath();
    ctx.moveTo(planetX - radius, bandY);
    ctx.bezierCurveTo(planetX - radius * 0.34, bandY - radius * 0.08, planetX + radius * 0.28, bandY + radius * 0.07, planetX + radius, bandY - radius * 0.025);
    ctx.stroke();
  }
  ctx.restore();

  const hubX = width * 0.58;
  const hubY = height * 0.3;
  const hubRadius = height * 0.074;
  const spokeAngles = [-2.75, -2.1, -1.42, -0.68, 0.04, 0.72, 1.38, 2.2];
  for (let index = 0; index < spokeAngles.length; index += 1) {
    const angle = spokeAngles[index];
    const distance = height * (index % 2 ? 0.23 : 0.19);
    const moduleX = hubX + Math.cos(angle) * distance;
    const moduleY = hubY + Math.sin(angle) * distance * 0.64;
    drawBeam(ctx, hubX + Math.cos(angle) * hubRadius * 0.7, hubY + Math.sin(angle) * hubRadius * 0.7, moduleX, moduleY, Math.max(5, height * 0.009), palette, index % 3 === 0 ? palette.red : palette.cyan, 0.9);
    drawOrbitalModule(ctx, moduleX, moduleY, height * (index % 2 ? 0.116 : 0.092), height * 0.045, angle, palette, index % 3 === 0 ? palette.red : palette.cyan, 0.96);
  }
  ctx.save();
  ctx.translate(hubX, hubY);
  const hubGradient = ctx.createRadialGradient(-hubRadius * 0.25, -hubRadius * 0.25, hubRadius * 0.08, 0, 0, hubRadius);
  hubGradient.addColorStop(0, rgba(palette.steel, 0.98));
  hubGradient.addColorStop(0.48, rgba(palette.gunmetal, 0.98));
  hubGradient.addColorStop(1, rgba(palette.black, 1));
  ctx.fillStyle = hubGradient;
  ctx.beginPath();
  for (let side = 0; side < 8; side += 1) {
    const angle = -Math.PI / 8 + side * TAU / 8;
    const x = Math.cos(angle) * hubRadius;
    const y = Math.sin(angle) * hubRadius;
    if (side === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(palette.cyan, 0.58);
  ctx.lineWidth = 2;
  ctx.stroke();
  drawPanel(ctx, -hubRadius * 0.46, -hubRadius * 0.22, hubRadius * 0.92, hubRadius * 0.44, palette, palette.cyan, 0.92);
  ctx.restore();
  const deckY = height * 0.69;
  const deck = ctx.createLinearGradient(0, deckY, 0, height);
  deck.addColorStop(0, "#252d33");
  deck.addColorStop(0.22, "#10161a");
  deck.addColorStop(1, "#030506");
  ctx.fillStyle = deck;
  ctx.beginPath();
  ctx.moveTo(width * 0.02, deckY);
  ctx.lineTo(width * 0.95, deckY - height * 0.06);
  ctx.lineTo(width, height);
  ctx.lineTo(0, height);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(palette.steel, 0.22);
  ctx.lineWidth = 1;
  for (let index = 0; index < 9; index += 1) {
    const x = (index / 8) * width;
    ctx.beginPath();
    ctx.moveTo(width * 0.48, deckY - height * 0.03);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  ctx.strokeStyle = rgba(palette.steel, 0.38);
  ctx.lineWidth = Math.max(2, height * 0.004);
  ctx.beginPath();
  ctx.moveTo(0, height * 0.21);
  ctx.lineTo(width * 0.22, height * 0.08);
  ctx.lineTo(width * 0.55, height * 0.12);
  ctx.lineTo(width * 0.78, height * 0.03);
  ctx.stroke();
  for (let x = 0.08; x < 0.88; x += 0.16) {
    ctx.beginPath();
    ctx.moveTo(width * x, height * 0.16);
    ctx.lineTo(width * (x + 0.08), height * 0.34);
    ctx.lineTo(width * (x + 0.16), height * 0.13);
    ctx.stroke();
  }
  drawBeam(ctx, 0, deckY - height * 0.005, width, deckY - height * 0.07, height * 0.011, palette, palette.cyan, 0.82);
  for (let post = 0; post < 9; post += 1) {
    const x = width * (0.04 + post * 0.115);
    drawBeam(ctx, x, deckY - height * 0.006, x, deckY - height * 0.086, height * 0.006, palette, palette.steel, 0.64);
  }  ctx.restore();
}

function drawShuttle(ctx, x, y, scale, palette, opacity, direction = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(direction, 1);
  const hull = ctx.createLinearGradient(-scale, 0, scale, 0);
  hull.addColorStop(0, rgba(palette.black, opacity));
  hull.addColorStop(0.42, rgba(palette.steel, opacity * 0.82));
  hull.addColorStop(1, rgba(palette.gunmetal, opacity));
  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(scale * 0.82, 0);
  ctx.lineTo(scale * 0.28, -scale * 0.18);
  ctx.lineTo(-scale * 0.7, -scale * 0.1);
  ctx.lineTo(-scale, scale * 0.14);
  ctx.lineTo(scale * 0.32, scale * 0.12);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(palette.cyan, opacity * 0.55);
  ctx.lineWidth = Math.max(0.6, scale * 0.025);
  ctx.stroke();
  ctx.fillStyle = rgba(palette.red, opacity * 0.76);
  ctx.fillRect(-scale * 0.86, -scale * 0.02, scale * 0.16, scale * 0.06);
  ctx.restore();
}

function drawOrbital(ctx, width, height, data, scene, time, parallax, cueStrength) {
  const palette = scene.palette;
  const source = scene.source;
  const secondary = scene.secondaryMotion;
  const response = scene.environmentalResponse;
  const atmosphere = scene.decayAtmosphere;

  if (source.enabled) {
    const lensX = width * 0.68 + parallax.x * -8;
    const lensY = height * 0.35 + parallax.y * -5;
    const breathe = 1 + Math.sin(time * source.speed) * 0.025;
    ctx.save();
    ctx.translate(lensX, lensY);
    ctx.scale(1.5 * breathe, 0.65 / breathe);
    const lens = ctx.createRadialGradient(0, 0, 0, 0, 0, height * 0.16);
    lens.addColorStop(0, rgba(palette.black, 0.92));
    lens.addColorStop(0.48, rgba(palette.blue, source.opacity * 0.08));
    lens.addColorStop(0.83, rgba(palette.cyan, source.opacity * (0.22 + cueStrength * 0.18)));
    lens.addColorStop(0.91, rgba(palette.white, source.opacity * 0.38));
    lens.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = lens;
    ctx.beginPath();
    ctx.arc(0, 0, height * 0.09, 0, TAU);
    ctx.fill();
    ctx.restore();

    // A turbulent particulate lens keeps the focal mechanism dimensional instead of reading as one blurred oval.
    ctx.save();
    ctx.translate(lensX, lensY);
    ctx.globalCompositeOperation = "lighter";
    for (let index = 0; index < 42; index += 1) {
      const seed = hash(scene.seed ^ 0x41b17, index + 401);
      const phase = seed * TAU + time * source.speed * (0.075 + seed * 0.055);
      const band = height * (0.052 + hash(scene.seed, index + 901) * 0.036);
      const x = Math.cos(phase) * band * 2.7;
      const y = Math.sin(phase) * band * 0.58;
      const tangentX = -Math.sin(phase) * (7 + seed * 13);
      const tangentY = Math.cos(phase) * (1.6 + seed * 4.2);
      ctx.strokeStyle = rgba(index % 7 === 0 ? palette.white : index % 3 === 0 ? palette.blue : palette.cyan, source.opacity * (0.08 + seed * 0.19));
      ctx.lineWidth = 0.45 + seed * 0.8;
      ctx.beginPath();
      ctx.moveTo(x - tangentX, y - tangentY);
      ctx.quadraticCurveTo(x, y + Math.sin(phase * 3) * 3, x + tangentX, y + tangentY);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
    for (let bandIndex = 0; bandIndex < 3; bandIndex += 1) {
      const radiusX = height * (0.31 + bandIndex * 0.021);
      const radiusY = height * (0.106 + bandIndex * 0.011);
      const rotation = -0.075 + bandIndex * 0.043;
      ctx.strokeStyle = rgba(bandIndex === 1 ? palette.white : palette.cyan, source.opacity * (0.1 + bandIndex * 0.055));
      ctx.lineWidth = bandIndex === 1 ? 1.25 : 0.7;
      ctx.setLineDash([height * (0.025 + bandIndex * 0.008), height * (0.011 + bandIndex * 0.006)]);
      ctx.beginPath();
      ctx.ellipse(0, 0, radiusX, radiusY, rotation, Math.PI * (1.05 + bandIndex * 0.08), Math.PI * (1.82 + bandIndex * 0.06));
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
    ctx.strokeStyle = rgba(palette.cyan, source.opacity * 0.32);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(lensX - width * 0.19, lensY + height * 0.012);
    ctx.bezierCurveTo(lensX - width * 0.08, lensY - height * 0.065, lensX + width * 0.09, lensY + height * 0.07, lensX + width * 0.2, lensY - height * 0.014);
    ctx.stroke();
  }

  if (secondary.enabled) {
    for (let index = 0; index < data.shuttles.length; index += 1) {
      const shuttle = data.shuttles[index];
      const progress = (shuttle.base + time * shuttle.speed * secondary.speed) % 1;
      const x = mix(-width * 0.12, width * 1.13, progress) + parallax.x * shuttle.depth * 5;
      const y = height * shuttle.elevation + Math.sin(progress * Math.PI) * -height * 0.07 + parallax.y * shuttle.depth * 4;
      const scale = height * (0.038 + shuttle.depth * 0.034 + (index === 0 ? 0.015 : 0));
      ctx.strokeStyle = rgba(palette.cyan, secondary.opacity * shuttle.depth * 0.14);
      ctx.lineWidth = Math.max(0.5, shuttle.depth);
      ctx.beginPath();
      ctx.moveTo(x - scale * 4.5, y);
      ctx.lineTo(x - scale, y);
      ctx.stroke();
      drawShuttle(ctx, x, y, scale, palette, secondary.opacity * (0.35 + shuttle.depth * 0.65), 1);
    }

    const deckY = height * 0.69;
    if (scene.motion?.proceduralPeople !== false) for (let index = 0; index < data.walkers.length; index += 1) {
      const person = data.walkers[index];
      const conversation = index < 6;
      const pairIndex = Math.floor(index / 2);
      const baseX = width * (0.22 + pairIndex * 0.085);
      let x;
      let facing;
      let mode;
      if (conversation) {
        x = baseX + (index % 2 === 0 ? -height * 0.028 : height * 0.028);
        facing = index % 2 === 0 ? 1 : -1;
        mode = "gesture";
      } else {
        const progress = (person.base + time * person.speed * 0.32) % 1;
        x = mix(width * 0.08, width * 0.92, progress);
        facing = 1;
        mode = "walk";
      }
      const depth = clamp((x / width) * 0.35 + 0.45);
      const footY = deckY + height * 0.04 + (x / width) * height * 0.02;
      const stature = height * (0.078 + depth * 0.014) * person.height;
      const phase = time * (2.1 + person.speed * 8) + person.phase;
      drawPerson(ctx, x + parallax.x * 3, footY, stature, phase, facing, palette, secondary.opacity * 0.84, mode);
    }
  }

  if (response.enabled) {
    for (let index = 0; index < 7; index += 1) {
      const x = width * (0.08 + index * 0.135);
      const blink = Math.pow(clamp(Math.sin(time * 1.35 + index * 1.73) * 0.5 + 0.5), 8);
      const beacon = ctx.createRadialGradient(x, height * 0.66, 0, x, height * 0.66, height * 0.055);
      beacon.addColorStop(0, rgba(index % 3 === 0 ? palette.red : palette.cyan, response.opacity * (0.32 + blink * 0.6)));
      beacon.addColorStop(1, rgba(palette.cyan, 0));
      ctx.fillStyle = beacon;
      ctx.fillRect(x - height * 0.055, height * 0.605, height * 0.11, height * 0.11);
    }
  }
  if (atmosphere.enabled) drawAtmosphere(ctx, width, height, data, scene, time, parallax, "space");
}

function drawFoundryStatic(ctx, width, height, data, scene) {
  const palette = scene.palette;
  const primary = scene.primaryEnvironment;
  const background = ctx.createLinearGradient(0, 0, 0, height);
  background.addColorStop(0, "#050708");
  background.addColorStop(0.52, "#0d1114");
  background.addColorStop(1, "#020303");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);
  if (!primary.enabled) return;
  ctx.save();
  ctx.globalAlpha = clamp(Number(primary.opacity ?? 1));

  for (let index = 0; index < 13; index += 1) {
    const x = (index / 12) * width;
    const towerHeight = height * mix(0.16, 0.45, hash(scene.seed, index + 20));
    const towerWidth = width * mix(0.028, 0.07, hash(scene.seed, index + 50));
    const topY = height * 0.58 - towerHeight;
    const tower = ctx.createLinearGradient(x - towerWidth / 2, 0, x + towerWidth / 2, 0);
    tower.addColorStop(0, "#080a0b");
    tower.addColorStop(0.52, "#252a2e");
    tower.addColorStop(1, "#050607");
    ctx.fillStyle = tower;
    ctx.fillRect(x - towerWidth / 2, topY, towerWidth, towerHeight);
    ctx.strokeStyle = rgba(index % 4 === 0 ? palette.red : palette.steel, 0.2);
    ctx.lineWidth = 1;
    ctx.strokeRect(x - towerWidth / 2, topY, towerWidth, towerHeight);
    if (index % 3 === 0) {
      ctx.fillStyle = rgba(palette.gunmetal, 0.95);
      ctx.fillRect(x - towerWidth * 0.12, topY - height * 0.1, towerWidth * 0.24, height * 0.1);
    }
  }

  const reactorX = width * 0.49;
  const reactorY = height * 0.5;
  const chamberWidth = width * 0.255;
  const chamberHeight = height * 0.48;
  const chamberTop = reactorY - chamberHeight / 2;
  const chamberBottom = reactorY + chamberHeight / 2;
  const chamberLeft = reactorX - chamberWidth / 2;
  const chamberRight = reactorX + chamberWidth / 2;
  drawPanel(ctx, chamberLeft, chamberTop, chamberWidth, chamberHeight, palette, palette.red, 0.96);
  ctx.fillStyle = rgba(palette.black, 0.98);
  roundedRectPath(ctx, chamberLeft + chamberWidth * 0.14, chamberTop + chamberHeight * 0.12, chamberWidth * 0.72, chamberHeight * 0.7, height * 0.012);
  ctx.fill();
  ctx.strokeStyle = rgba(palette.cyan, 0.34);
  ctx.lineWidth = 1.3;
  ctx.stroke();
  for (const side of [-1, 1]) {
    const columnX = reactorX + side * chamberWidth * 0.59;
    drawBeam(ctx, columnX, chamberTop - height * 0.04, columnX, chamberBottom + height * 0.05, height * 0.027, palette, side < 0 ? palette.red : palette.cyan, 0.96);
    for (let coupler = 0; coupler < 5; coupler += 1) {
      const y = chamberTop + chamberHeight * (0.15 + coupler * 0.17);
      drawBeam(ctx, columnX, y, reactorX + side * chamberWidth * 0.38, y, height * 0.012, palette, coupler % 2 ? palette.cyan : palette.red, 0.84);
    }
  }
  drawBeam(ctx, chamberLeft - chamberWidth * 0.08, chamberTop - height * 0.035, chamberRight + chamberWidth * 0.08, chamberTop - height * 0.035, height * 0.035, palette, palette.cyan, 0.96);
  drawBeam(ctx, chamberLeft - chamberWidth * 0.1, chamberBottom + height * 0.035, chamberRight + chamberWidth * 0.1, chamberBottom + height * 0.035, height * 0.05, palette, palette.red, 0.98);
  for (let pipeIndex = 0; pipeIndex < 3; pipeIndex += 1) {
    const y = chamberTop + chamberHeight * (0.2 + pipeIndex * 0.27);
    for (const side of [-1, 1]) {
      ctx.strokeStyle = rgba(pipeIndex % 2 ? palette.cyan : palette.steel, 0.44);
      ctx.lineWidth = height * (0.009 + pipeIndex * 0.003);
      ctx.beginPath();
      ctx.moveTo(reactorX + side * chamberWidth * 0.64, y);
      ctx.bezierCurveTo(reactorX + side * chamberWidth * 0.86, y, width * (side < 0 ? 0.21 : 0.77), y - height * 0.07, width * (side < 0 ? 0.04 : 0.96), y + height * 0.02);
      ctx.stroke();
    }
  }
  const gantryY = height * 0.15;
  drawBeam(ctx, width * 0.05, gantryY, width * 0.93, gantryY, height * 0.038, palette, palette.red, 0.98);
  drawBeam(ctx, width * 0.09, height * 0.08, width * 0.09, height * 0.7, height * 0.032, palette, palette.steel, 0.9);
  drawBeam(ctx, width * 0.88, height * 0.08, width * 0.88, height * 0.7, height * 0.032, palette, palette.steel, 0.9);
  for (let truss = 0; truss < 8; truss += 1) {
    const x = width * (0.08 + truss * 0.105);
    drawBeam(ctx, x, gantryY - height * 0.02, x + width * 0.052, gantryY + height * 0.04, height * 0.008, palette, truss % 3 === 0 ? palette.red : palette.steel, 0.68);
  }
  const floorY = height * 0.66;
  const floor = ctx.createLinearGradient(0, floorY, 0, height);
  floor.addColorStop(0, "#25282a");
  floor.addColorStop(0.1, "#111416");
  floor.addColorStop(1, "#030404");
  ctx.fillStyle = floor;
  ctx.fillRect(0, floorY, width, height - floorY);
  ctx.strokeStyle = rgba(palette.steel, 0.2);
  for (let index = 0; index <= 12; index += 1) {
    const x = (index / 12) * width;
    ctx.beginPath();
    ctx.moveTo(width * 0.5, floorY);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  ctx.lineWidth = Math.max(3, height * 0.008);
  ctx.strokeStyle = rgba(palette.gunmetal, 0.98);
  ctx.beginPath();
  ctx.moveTo(-width * 0.04, height * 0.25);
  ctx.lineTo(width * 0.23, height * 0.11);
  ctx.lineTo(width * 0.5, height * 0.28);
  ctx.lineTo(width * 0.78, height * 0.08);
  ctx.lineTo(width * 1.04, height * 0.23);
  ctx.stroke();
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = rgba(palette.steel, 0.32);
  for (let index = 0; index < 8; index += 1) {
    const x = width * (index / 7);
    ctx.beginPath();
    ctx.moveTo(x, height * 0.18);
    ctx.lineTo(x + width * 0.1, height * 0.44);
    ctx.stroke();
  }

  ctx.fillStyle = "#090b0c";
  ctx.fillRect(0, height * 0.51, width, height * 0.045);
  ctx.strokeStyle = rgba(palette.steel, 0.28);
  ctx.lineWidth = 1;
  ctx.strokeRect(0, height * 0.51, width, height * 0.045);
  for (let x = 0; x < width; x += Math.max(20, width * 0.032)) {
    ctx.beginPath();
    ctx.moveTo(x, height * 0.555);
    ctx.lineTo(x + height * 0.025, height * 0.51);
    ctx.stroke();
  }
  const activeCatwalkY = height * 0.68;
  drawBeam(ctx, 0, activeCatwalkY, width, activeCatwalkY, height * 0.027, palette, palette.cyan, 0.94);
  drawBeam(ctx, 0, activeCatwalkY - height * 0.084, width, activeCatwalkY - height * 0.084, height * 0.009, palette, palette.steel, 0.74);
  for (let post = 0; post < 18; post += 1) {
    const x = width * (post / 17);
    drawBeam(ctx, x, activeCatwalkY, x, activeCatwalkY - height * 0.084, height * 0.006, palette, palette.steel, 0.6);
  }  ctx.restore();
}

function drawFoundry(ctx, width, height, data, scene, time, parallax, cueStrength) {
  const palette = scene.palette;
  const source = scene.source;
  const secondary = scene.secondaryMotion;
  const response = scene.environmentalResponse;
  const atmosphere = scene.decayAtmosphere;
  const coreX = width * 0.49 + parallax.x * -5;
  const coreY = height * 0.55 + parallax.y * -2;
  const chamberWidth = width * 0.255;
  const chamberHeight = height * 0.48;

  if (source.enabled) {
    const pulse = 0.78 + 0.16 * Math.sin(time * source.speed * 2.1) + cueStrength * 0.18;
    const heat = ctx.createRadialGradient(coreX, coreY, 0, coreX, coreY, height * 0.25);
    heat.addColorStop(0, rgba(palette.white, source.opacity * pulse));
    heat.addColorStop(0.045, rgba(palette.ember, source.opacity * 0.92));
    heat.addColorStop(0.18, rgba(palette.red, source.opacity * 0.45));
    heat.addColorStop(0.55, rgba(palette.red, source.opacity * 0.08));
    heat.addColorStop(1, rgba(palette.red, 0));
    ctx.fillStyle = heat;
    ctx.fillRect(coreX - height * 0.25, coreY - height * 0.25, height * 0.5, height * 0.5);
    ctx.fillStyle = rgba(palette.black, 0.92);
    ctx.beginPath();
    ctx.moveTo(coreX - height * 0.08, coreY + height * 0.08);
    ctx.lineTo(coreX - height * 0.045, coreY - height * 0.11);
    ctx.lineTo(coreX + height * 0.055, coreY - height * 0.11);
    ctx.lineTo(coreX + height * 0.085, coreY + height * 0.08);
    ctx.closePath();
    ctx.fill();
    // Segmented containment rails, attached couplers, and braided energy make this read as a working reactor.
    ctx.save();
    ctx.translate(coreX, coreY - height * 0.015);
    const reactorHalfWidth = height * 0.067;
    const reactorTop = -height * 0.115;
    const reactorBottom = height * 0.092;
    const railWidth = height * 0.015;
    for (const side of [-1, 1]) {
      const railX = side * reactorHalfWidth - railWidth / 2;
      const rail = ctx.createLinearGradient(railX, 0, railX + side * railWidth, 0);
      rail.addColorStop(0, rgba(palette.black, 0.98));
      rail.addColorStop(0.48, rgba(palette.steel, 0.68));
      rail.addColorStop(1, rgba(palette.gunmetal, 0.98));
      ctx.fillStyle = rail;
      ctx.fillRect(railX, reactorTop, railWidth, reactorBottom - reactorTop);
      ctx.strokeStyle = rgba(side > 0 ? palette.cyan : palette.red, 0.42);
      ctx.lineWidth = 0.8;
      ctx.strokeRect(railX, reactorTop, railWidth, reactorBottom - reactorTop);
      for (let segment = 0; segment < 5; segment += 1) {
        const y = reactorTop + height * (0.018 + segment * 0.043);
        const couplerLength = height * (0.026 + (segment % 2) * 0.008);
        ctx.fillStyle = rgba(palette.gunmetal, 0.94);
        ctx.fillRect(side > 0 ? railX - couplerLength : railX + railWidth, y, couplerLength, height * 0.012);
        ctx.strokeStyle = rgba(segment % 2 ? palette.cyan : palette.ember, 0.34);
        ctx.beginPath();
        ctx.moveTo(side > 0 ? railX - couplerLength : railX + railWidth, y + height * 0.006);
        ctx.lineTo(side > 0 ? railX : railX + railWidth, y + height * 0.006);
        ctx.stroke();
        ctx.fillStyle = rgba(palette.white, 0.32);
        ctx.beginPath();
        ctx.arc(side > 0 ? railX + railWidth * 0.5 : railX + railWidth * 0.5, y + height * 0.006, Math.max(0.8, height * 0.0017), 0, TAU);
        ctx.fill();
      }
    }
    for (const capY of [reactorTop, reactorBottom]) {
      const cap = ctx.createLinearGradient(-reactorHalfWidth, capY, reactorHalfWidth, capY);
      cap.addColorStop(0, rgba(palette.black, 0.96));
      cap.addColorStop(0.22, rgba(palette.steel, 0.62));
      cap.addColorStop(0.5, rgba(palette.gunmetal, 0.96));
      cap.addColorStop(0.78, rgba(palette.steel, 0.62));
      cap.addColorStop(1, rgba(palette.black, 0.96));
      ctx.fillStyle = cap;
      ctx.fillRect(-reactorHalfWidth - railWidth * 0.35, capY - height * 0.008, reactorHalfWidth * 2 + railWidth * 0.7, height * 0.016);
      ctx.strokeStyle = rgba(capY < 0 ? palette.cyan : palette.red, 0.36);
      ctx.strokeRect(-reactorHalfWidth - railWidth * 0.35, capY - height * 0.008, reactorHalfWidth * 2 + railWidth * 0.7, height * 0.016);
    }
    ctx.globalCompositeOperation = "lighter";
    for (let filament = 0; filament < 9; filament += 1) {
      const offset = (filament - 4) * height * 0.006;
      const sway = Math.sin(time * (1.15 + filament * 0.07) + filament * 1.7) * height * 0.011;
      const top = reactorTop + height * 0.017 + (filament % 2) * height * 0.008;
      const bottom = reactorBottom - height * 0.016 - ((filament + 1) % 3) * height * 0.006;
      const energy = filament % 4 === 0 ? palette.cyan : filament % 3 === 0 ? palette.white : palette.ember;
      ctx.strokeStyle = rgba(energy, source.opacity * (0.18 + (filament % 3) * 0.085));
      ctx.lineWidth = filament % 4 === 0 ? 1.35 : 0.7;
      ctx.beginPath();
      ctx.moveTo(offset, top);
      ctx.bezierCurveTo(offset + sway, top * 0.4, offset - sway * 0.75, bottom * 0.2, offset + sway * 0.2, bottom);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();
  }

  if (secondary.enabled) {
    const trolleyProgress = 0.5 + 0.5 * Math.sin(time * secondary.speed * 0.22);
    const trolleyX = mix(width * 0.2, width * 0.76, trolleyProgress);
    const trolleyY = height * 0.15;
    drawPanel(ctx, trolleyX - width * 0.035, trolleyY - height * 0.023, width * 0.07, height * 0.046, palette, palette.red, secondary.opacity * 0.96);
    const cableLength = height * (0.16 + 0.075 * (0.5 + 0.5 * Math.sin(time * 0.31 + 0.8)));
    ctx.strokeStyle = rgba(palette.steel, secondary.opacity * 0.75);
    ctx.lineWidth = Math.max(1.2, height * 0.0028);
    ctx.beginPath();
    ctx.moveTo(trolleyX - width * 0.009, trolleyY + height * 0.023);
    ctx.lineTo(trolleyX - width * 0.009, trolleyY + cableLength);
    ctx.moveTo(trolleyX + width * 0.009, trolleyY + height * 0.023);
    ctx.lineTo(trolleyX + width * 0.009, trolleyY + cableLength);
    ctx.stroke();
    drawPanel(ctx, trolleyX - width * 0.022, trolleyY + cableLength, width * 0.044, height * 0.027, palette, palette.cyan, secondary.opacity * 0.92);
    drawRobotArm(ctx, width * 0.25, height * 0.61, height * 0.13, time, 0.4, palette, secondary.opacity * 0.94, 1);
    drawRobotArm(ctx, width * 0.73, height * 0.61, height * 0.12, time, 2.1, palette, secondary.opacity * 0.9, -1);

    const catwalkY = height * 0.68;
    if (scene.motion?.proceduralPeople !== false) for (let index = 0; index < data.workers.length; index += 1) {
      const worker = data.workers[index];
      const progress = (worker.base + time * worker.speed * secondary.speed * 0.48) % 1;
      const x = mix(width * 0.03, width * 0.97, progress);
      const mode = index % 4 === 0 ? "gesture" : "walk";
      const phase = worker.phase + progress * TAU * 5.2;
      drawPerson(ctx, x + parallax.x * 2, catwalkY, height * 0.082 * worker.height, phase, worker.facing, palette, secondary.opacity * 0.96, mode);
    }

    const conveyorY = height * 0.8;
    drawBeam(ctx, width * 0.05, conveyorY, width * 0.93, conveyorY, height * 0.036, palette, palette.red, secondary.opacity * 0.9);
    for (let pallet = 0; pallet < 9; pallet += 1) {
      const progress = (pallet / 9 + time * secondary.speed * 0.025) % 1;
      const x = mix(width * 0.08, width * 0.9, progress);
      drawPanel(ctx, x - width * 0.026, conveyorY - height * 0.052, width * 0.052, height * 0.037, palette, pallet % 3 === 0 ? palette.cyan : palette.red, secondary.opacity * 0.8);
    }
    for (let index = 0; index < data.sparks.length; index += 1) {
      const spark = data.sparks[index];
      const age = ((time * secondary.speed + spark.phase * 3.1) % spark.life) / spark.life;
      if (age > 0.82) continue;
      const localX = coreX + spark.vx * height * age * 1.25;
      const localY = coreY + spark.vy * height * age + height * 0.32 * age * age;
      const alpha = secondary.opacity * spark.depth * Math.sin((age / 0.82) * Math.PI) * 0.8;
      ctx.strokeStyle = rgba(age < 0.22 ? palette.white : palette.ember, alpha);
      ctx.lineWidth = spark.size * spark.depth;
      ctx.beginPath();
      ctx.moveTo(localX, localY);
      ctx.lineTo(localX - spark.vx * height * 0.05, localY - spark.vy * height * 0.05);
      ctx.stroke();
    }

    for (let conduit = 0; conduit < 4; conduit += 1) {
      const offset = (conduit - 1.5) * chamberWidth * 0.16;
      const sway = Math.sin(time * (0.42 + conduit * 0.07) + conduit) * chamberWidth * 0.04;
      ctx.strokeStyle = rgba(conduit % 2 ? palette.red : palette.cyan, secondary.opacity * 0.16);
      ctx.lineWidth = conduit % 2 ? 0.8 : 1.1;
      ctx.beginPath();
      ctx.moveTo(coreX + offset, coreY - chamberHeight * 0.32);
      ctx.bezierCurveTo(coreX + offset + sway, coreY - chamberHeight * 0.1, coreX + offset - sway, coreY + chamberHeight * 0.12, coreX + offset * 0.8, coreY + chamberHeight * 0.3);
      ctx.stroke();
    }
  }

  if (response.enabled) {
    const flicker = 0.6 + 0.4 * Math.sin(time * 3.7) * Math.sin(time * 1.13 + 1.2);
    const floorLight = ctx.createLinearGradient(0, height * 0.61, 0, height);
    floorLight.addColorStop(0, rgba(palette.red, response.opacity * (0.05 + cueStrength * 0.08)));
    floorLight.addColorStop(0.48, rgba(palette.ember, response.opacity * 0.025 * flicker));
    floorLight.addColorStop(1, rgba(palette.red, 0));
    ctx.fillStyle = floorLight;
    ctx.fillRect(0, height * 0.61, width, height * 0.39);

    const scanY = height * (0.28 + ((time * 0.027) % 1) * 0.42);
    const scanFog = ctx.createLinearGradient(0, scanY - height * 0.045, 0, scanY + height * 0.055);
    scanFog.addColorStop(0, rgba(palette.cyan, 0));
    scanFog.addColorStop(0.42, rgba(palette.cyan, response.opacity * 0.024));
    scanFog.addColorStop(0.5, rgba(palette.white, response.opacity * 0.05));
    scanFog.addColorStop(0.62, rgba(palette.red, response.opacity * 0.02));
    scanFog.addColorStop(1, rgba(palette.red, 0));
    ctx.fillStyle = scanFog;
    ctx.fillRect(0, scanY - height * 0.045, width, height * 0.1);

    for (let index = 0; index < 4; index += 1) {
      const shaftX = width * (0.12 + index * 0.23) + Math.sin(time * 0.08 + index) * width * 0.015;
      const shaft = ctx.createLinearGradient(shaftX, 0, shaftX + width * 0.07, 0);
      shaft.addColorStop(0, rgba(index % 2 ? palette.red : palette.cyan, 0));
      shaft.addColorStop(0.5, rgba(index % 2 ? palette.red : palette.cyan, response.opacity * 0.045));
      shaft.addColorStop(1, rgba(index % 2 ? palette.red : palette.cyan, 0));
      ctx.fillStyle = shaft;
      ctx.beginPath();
      ctx.moveTo(shaftX, 0);
      ctx.lineTo(shaftX + width * 0.07, 0);
      ctx.lineTo(shaftX + width * 0.17, height);
      ctx.lineTo(shaftX - width * 0.08, height);
      ctx.closePath();
      ctx.fill();
    }
  }
  if (atmosphere.enabled) drawAtmosphere(ctx, width, height, data, scene, time, parallax, "foundry");
}

const CINEMATIC_EVENT_DURATION = 7.5;

/**
 * One deterministic dramatic cycle shared by the three concept-specific effects.
 * The named stages deliberately land at the capture checkpoints used by visual QA:
 * 0s idle, 1.5s anticipation, 3s activation, 4.5s peak, 5.2s response and 6s decay.
 */
function cinematicLifecycle(time, cueStrength = 0, reducedMotion = false) {
  const localTime = ((Number(time) || 0) % CINEMATIC_EVENT_DURATION + CINEMATIC_EVENT_DURATION) % CINEMATIC_EVENT_DURATION;
  let phase = "idle";
  let phaseT = localTime / 0.8;
  let energy = 0.075 + 0.025 * Math.sin(localTime * 2.1) ** 2;
  let charge = 0;
  let strike = 0;
  let response = 0;
  let residue = 0;

  if (localTime >= 0.8 && localTime < 2.25) {
    phase = "anticipation";
    phaseT = smoothstep(0.8, 2.25, localTime);
    charge = phaseT;
    energy = mix(0.12, 0.44, phaseT);
  } else if (localTime >= 2.25 && localTime < 3.65) {
    phase = "activation";
    phaseT = smoothstep(2.25, 3.65, localTime);
    charge = 1;
    strike = smoothstep(0, 0.36, phaseT);
    energy = mix(0.48, 0.91, phaseT);
  } else if (localTime >= 3.65 && localTime < 4.9) {
    phase = "peak";
    phaseT = smoothstep(3.65, 4.9, localTime);
    charge = 1;
    strike = 1;
    energy = 0.94 + Math.sin(phaseT * Math.PI) * 0.06;
  } else if (localTime >= 4.9 && localTime < 5.75) {
    phase = "secondary-response";
    phaseT = smoothstep(4.9, 5.75, localTime);
    strike = 1 - phaseT * 0.38;
    response = Math.sin(phaseT * Math.PI);
    energy = mix(0.84, 0.52, phaseT);
  } else if (localTime >= 5.75) {
    phase = "decay";
    phaseT = smoothstep(5.75, CINEMATIC_EVENT_DURATION, localTime);
    response = 1 - phaseT;
    residue = 1 - phaseT;
    energy = mix(0.42, 0.055, phaseT);
  }

  const cue = clamp(Number(cueStrength) || 0);
  const restraint = reducedMotion ? 0.46 : 1;
  return {
    phase,
    phaseT,
    localTime,
    energy: clamp((energy + cue * 0.24) * restraint),
    charge: clamp((charge + cue * 0.18) * restraint),
    strike: clamp((strike + cue * 0.28) * restraint),
    response: clamp((response + cue * 0.2) * restraint),
    residue: clamp((residue + cue * 0.12) * restraint),
    cue,
    motion: reducedMotion ? 0.16 : 1,
    density: reducedMotion ? 0.42 : 1,
  };
}

function strokeLayeredEnergy(ctx, buildPath, color, coreColor, width, opacity, blur = 14) {
  if (opacity <= 0.001 || width <= 0.01) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  buildPath();
  ctx.strokeStyle = rgba(color, opacity * 0.16);
  ctx.lineWidth = width * 3.6;
  ctx.shadowColor = rgba(color, opacity * 0.7);
  ctx.shadowBlur = blur;
  ctx.stroke();
  ctx.shadowBlur = blur * 0.42;
  buildPath();
  ctx.strokeStyle = rgba(color, opacity * 0.7);
  ctx.lineWidth = width * 1.45;
  ctx.stroke();
  ctx.shadowBlur = blur * 0.14;
  buildPath();
  ctx.strokeStyle = rgba(coreColor, opacity * 0.92);
  ctx.lineWidth = Math.max(0.55, width * 0.34);
  ctx.stroke();
  ctx.restore();
}

function ribbonGeometry(points, widthAt, scale = 1) {
  const left = [];
  const right = [];
  for (let index = 0; index < points.length; index += 1) {
    const previous = points[Math.max(0, index - 1)];
    const next = points[Math.min(points.length - 1, index + 1)];
    const dx = next[0] - previous[0];
    const dy = next[1] - previous[1];
    const length = Math.max(0.0001, Math.hypot(dx, dy));
    const width = Math.max(0.08, widthAt(index / Math.max(1, points.length - 1), index) * scale);
    const nx = (-dy / length) * width;
    const ny = (dx / length) * width;
    left.push([points[index][0] + nx, points[index][1] + ny]);
    right.push([points[index][0] - nx, points[index][1] - ny]);
  }
  return { left, right };
}

function traceRibbonPolygon(ctx, geometry) {
  ctx.beginPath();
  geometry.left.forEach(([x, y], index) => index ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
  for (let index = geometry.right.length - 1; index >= 0; index -= 1) {
    ctx.lineTo(geometry.right[index][0], geometry.right[index][1]);
  }
  ctx.closePath();
}

function tracePointRail(ctx, points) {
  ctx.beginPath();
  points.forEach(([x, y], index) => index ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
}

function drawMaterialEnergyRibbon(ctx, points, widthAt, colors, opacity, options = {}) {
  if (opacity <= 0.002 || points.length < 3) return;
  const corona = ribbonGeometry(points, widthAt, Number(options.coronaScale) || 2.8);
  const body = ribbonGeometry(points, widthAt, 1);
  const core = ribbonGeometry(points, widthAt, Number(options.coreScale) || 0.2);
  const start = points[0];
  const end = points[points.length - 1];
  const bodyGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  bodyGradient.addColorStop(0, rgba(colors.edge, opacity * 0.18));
  bodyGradient.addColorStop(0.24, rgba(colors.body, opacity * 0.64));
  bodyGradient.addColorStop(0.58, rgba(colors.hot, opacity * 0.88));
  bodyGradient.addColorStop(0.82, rgba(colors.body, opacity * 0.62));
  bodyGradient.addColorStop(1, rgba(colors.edge, opacity * 0.12));

  ctx.save();
  ctx.globalCompositeOperation = options.blend || "lighter";
  ctx.shadowColor = rgba(colors.body, opacity * (Number(options.coronaStrength) || 0.74));
  ctx.shadowBlur = Number(options.blur) || 18;
  ctx.fillStyle = rgba(colors.body, opacity * 0.075);
  traceRibbonPolygon(ctx, corona);
  ctx.fill();
  ctx.shadowBlur = (Number(options.blur) || 18) * 0.34;
  ctx.fillStyle = bodyGradient;
  traceRibbonPolygon(ctx, body);
  ctx.fill();
  ctx.shadowBlur = 3;
  ctx.fillStyle = rgba(colors.hot, opacity * (Number(options.coreStrength) || 0.78));
  traceRibbonPolygon(ctx, core);
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.strokeStyle = rgba(colors.hot, opacity * 0.44);
  ctx.lineWidth = Math.max(0.55, Number(options.bevelWidth) || 0.8);
  tracePointRail(ctx, body.left);
  ctx.stroke();
  ctx.strokeStyle = rgba(colors.edge, opacity * 0.34);
  tracePointRail(ctx, body.right);
  ctx.stroke();

  if (options.striations !== false) {
    const step = Math.max(3, Math.round(points.length / (Number(options.striationCount) || 8)));
    for (let index = step; index < points.length - 1; index += step) {
      const left = body.left[index];
      const right = body.right[index];
      const inset = 0.12 + hash(points.length * 137, index) * 0.16;
      ctx.strokeStyle = rgba(index % (step * 2) ? colors.edge : colors.hot, opacity * 0.2);
      ctx.lineWidth = 0.65;
      ctx.beginPath();
      ctx.moveTo(mix(left[0], right[0], inset), mix(left[1], right[1], inset));
      ctx.lineTo(mix(left[0], right[0], 1 - inset), mix(left[1], right[1], 1 - inset));
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawGhostlineLifecycle(ctx, width, height, scene, time, parallax, state) {
  const palette = scene.palette;
  const eventOpacity = clamp(Number(scene.source?.opacity ?? 1));
  const anchorX = width * 0.505 + parallax.x * 3;
  const anchorY = height * 0.535 + parallax.y * 1.5;
  const energy = state.energy * eventOpacity;
  const active = clamp(state.charge * 0.55 + state.strike * 0.86 + state.response * 0.35);
  const animatedTime = time * state.motion;
  const visibleRibbons = state.phase === "idle" ? 2 : state.phase === "anticipation" ? 3 + Math.round(state.phaseT * 2) : 7;

  ctx.save();
  // The wake occupies the world seam between the story and form, not the headline.
  ctx.beginPath();
  ctx.rect(width * 0.405, height * 0.08, width * 0.19, height * 0.86);
  ctx.clip();

  const wallLight = ctx.createRadialGradient(anchorX, anchorY, 0, anchorX, anchorY, height * (0.13 + active * 0.22));
  wallLight.addColorStop(0, rgba(palette.white, energy * 0.16));
  wallLight.addColorStop(0.1, rgba(palette.cyan, energy * 0.2));
  wallLight.addColorStop(0.38, rgba(palette.blue, energy * 0.085));
  wallLight.addColorStop(1, rgba(palette.cyan, 0));
  ctx.fillStyle = wallLight;
  ctx.fillRect(width * 0.4, height * 0.18, width * 0.2, height * 0.68);

  // Wall-bound scan veil: layered fabric-like sheets with a lit spine and grain.
  const veilAlpha = energy * (0.15 + state.charge * 0.55 + state.response * 0.18);
  for (let sheet = 0; sheet < (state.density < 0.6 ? 2 : 4); sheet += 1) {
    const seed = hash(scene.seed ^ 0x5aa71, sheet + 19);
    const side = sheet % 2 ? 1 : -1;
    const upperX = anchorX + side * width * (0.018 + seed * 0.028);
    const top = height * (0.1 + seed * 0.12);
    const sheetGradient = ctx.createLinearGradient(upperX, top, anchorX, anchorY);
    sheetGradient.addColorStop(0, rgba(palette.blue, 0));
    sheetGradient.addColorStop(0.44, rgba(sheet % 3 === 0 ? palette.red : palette.cyan, veilAlpha * 0.055));
    sheetGradient.addColorStop(0.76, rgba(palette.cyan, veilAlpha * 0.11));
    sheetGradient.addColorStop(1, rgba(palette.white, veilAlpha * 0.16));
    ctx.fillStyle = sheetGradient;
    const sway = Math.sin(animatedTime * (0.27 + seed * 0.12) + sheet * 2.4) * width * 0.008;
    ctx.beginPath();
    ctx.moveTo(upperX - width * 0.012, top);
    ctx.bezierCurveTo(upperX + sway, height * 0.27, anchorX - side * width * 0.026, height * 0.41, anchorX, anchorY);
    ctx.bezierCurveTo(anchorX + side * width * 0.012, height * 0.41, upperX + width * 0.018 + sway, height * 0.26, upperX + width * 0.013, top);
    ctx.closePath();
    ctx.fill();
  }

  // Broad signal skins supply refractive body and surface depth behind the fine conductors.
  for (let skin = 0; skin < (state.density < 0.6 ? 1 : 3); skin += 1) {
    const seed = hash(scene.seed ^ 0x7745, skin + 311);
    const endX = width * (0.44 + seed * 0.115);
    const endY = height * (0.88 + seed * 0.055);
    const points = [];
    const samples = state.density < 0.6 ? 18 : 32;
    for (let sample = 0; sample <= samples; sample += 1) {
      const t = sample / samples;
      const perspective = t * t;
      const bow = Math.sin(t * Math.PI * (1.2 + seed * 0.5) + skin * 1.8) * width * (0.003 + perspective * 0.01);
      points.push([
        mix(anchorX, endX, perspective) + bow,
        mix(anchorY, endY, perspective) + Math.sin(t * TAU * 2.7 + seed * 5) * height * 0.0025 * perspective,
      ]);
    }
    drawMaterialEnergyRibbon(
      ctx,
      points,
      (t) => height * (0.0018 + t * t * (0.006 + active * 0.004)) * (0.8 + seed * 0.3),
      { edge: skin === 2 ? palette.red : palette.blue, body: skin === 1 ? palette.blue : palette.cyan, hot: palette.cyan },
      energy * (0.12 + seed * 0.1),
      { blur: 12, striationCount: 10, coronaScale: 2.1, coreScale: 0.09, coreStrength: 0.34, blend: "screen" },
    );
  }

  // Perspective signal wake: strands emerge from one rail anchor and widen toward the foreground.
  for (let ribbon = 0; ribbon < visibleRibbons; ribbon += 1) {
    const seed = hash(scene.seed ^ 0x911ea, ribbon + 71);
    const endX = width * (0.425 + seed * 0.145);
    const endY = height * (0.865 + hash(scene.seed, ribbon + 441) * 0.065);
    const points = [];
    const samples = state.density < 0.6 ? 19 : 36;
    for (let sample = 0; sample <= samples; sample += 1) {
      const t = sample / samples;
      const perspective = t * t;
      const lateral = Math.sin(t * TAU * (1.15 + seed * 0.55) + ribbon * 1.71 + animatedTime * 0.18) * width * (0.002 + perspective * 0.009);
      const micro = Math.sin(t * TAU * (7.3 + seed * 2.1) + ribbon) * width * 0.0009 * (0.25 + active);
      points.push([
        mix(anchorX, endX, perspective) + lateral + micro,
        mix(anchorY, endY, perspective) + Math.sin(t * Math.PI) * height * (seed - 0.5) * 0.018,
      ]);
    }
    const opacity = energy * (0.34 + seed * 0.36) * (state.phase === "decay" ? 1 - state.phaseT * 0.42 : 1);
    drawMaterialEnergyRibbon(
      ctx,
      points,
      (t) => height * (0.0008 + t * t * (0.0028 + active * 0.0032)) * (0.72 + seed * 0.5),
      {
        edge: ribbon % 5 === 0 ? palette.red : palette.blue,
        body: ribbon % 4 === 0 ? palette.blue : palette.cyan,
        hot: ribbon % 3 === 0 ? palette.cyan : palette.white,
      },
      opacity,
      { blur: 10 + active * 9, striationCount: 7, coronaScale: 2.4 },
    );
  }

  // Perspective reflection and wall bounce tie the signal to the concourse surfaces.
  const floorReflection = ctx.createLinearGradient(anchorX, anchorY, anchorX, height * 0.96);
  floorReflection.addColorStop(0, rgba(palette.white, energy * 0.13));
  floorReflection.addColorStop(0.18, rgba(palette.cyan, energy * 0.09));
  floorReflection.addColorStop(0.72, rgba(palette.blue, energy * 0.025));
  floorReflection.addColorStop(1, rgba(palette.cyan, 0));
  ctx.fillStyle = floorReflection;
  ctx.beginPath();
  ctx.moveTo(anchorX - width * 0.006, anchorY);
  ctx.lineTo(anchorX + width * 0.008, anchorY);
  ctx.lineTo(width * 0.575, height * 0.95);
  ctx.lineTo(width * 0.41, height * 0.95);
  ctx.closePath();
  ctx.fill();

  ctx.save();
  ctx.translate(anchorX, height * 0.755);
  ctx.scale(1.45, 0.34);
  const contactPool = ctx.createRadialGradient(0, 0, 0, 0, 0, height * 0.15);
  contactPool.addColorStop(0, rgba(palette.white, energy * 0.14));
  contactPool.addColorStop(0.16, rgba(palette.cyan, energy * 0.2));
  contactPool.addColorStop(0.48, rgba(palette.blue, energy * 0.075));
  contactPool.addColorStop(1, rgba(palette.cyan, 0));
  ctx.fillStyle = contactPool;
  ctx.fillRect(-height * 0.15, -height * 0.15, height * 0.3, height * 0.3);
  ctx.restore();
  // Electrical branches terminate on visible structural seams instead of floating over text.
  const anchors = [
    [width * 0.438, height * 0.18],
    [width * 0.565, height * 0.25],
    [width * 0.445, height * 0.7],
    [width * 0.57, height * 0.74],
    [width * 0.552, height * 0.39],
  ];
  const branchCount = Math.round((3 + active * 8 + state.cue * 3) * state.density);
  for (let branch = 0; branch < branchCount; branch += 1) {
    const seed = hash(scene.seed ^ 0xe17c, branch + 131);
    const targetIndex = branch % anchors.length;
    const target = anchors[targetIndex];
    const branchColor = targetIndex === 3 ? palette.red : palette.cyan;
    const startY = anchorY + height * (0.035 + seed * 0.2);
    const startX = anchorX + (seed - 0.5) * width * 0.018;
    const path = () => {
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(mix(startX, target[0], 0.28), mix(startY, target[1], 0.28) + (seed - 0.5) * height * 0.03);
      ctx.lineTo(mix(startX, target[0], 0.57), mix(startY, target[1], 0.57) - (seed - 0.5) * height * 0.024);
      ctx.lineTo(mix(startX, target[0], 0.82), mix(startY, target[1], 0.82) + Math.sin(seed * 9) * height * 0.009);
      ctx.lineTo(target[0], target[1]);
    };
    strokeLayeredEnergy(ctx, path, branchColor, palette.white, Math.max(0.62, height * 0.0011), energy * active * (0.18 + seed * 0.28), 7);
    if (branch < anchors.length) {
      const contact = ctx.createRadialGradient(target[0], target[1], 0, target[0], target[1], height * 0.012);
      contact.addColorStop(0, rgba(palette.white, energy * active * 0.3));
      contact.addColorStop(0.24, rgba(branchColor, energy * active * 0.22));
      contact.addColorStop(1, rgba(palette.cyan, 0));
      ctx.fillStyle = contact;
      ctx.fillRect(target[0] - height * 0.012, target[1] - height * 0.012, height * 0.024, height * 0.024);
    }
  }
  ctx.restore();
}

function offAxisEllipsePoint(cx, cy, radiusX, radiusY, rotation, angle, radialOffset = 0) {
  const localX = Math.cos(angle) * (radiusX + radialOffset);
  const localY = Math.sin(angle) * (radiusY + radialOffset * 0.54);
  const cosine = Math.cos(rotation);
  const sine = Math.sin(rotation);
  return [cx + localX * cosine - localY * sine, cy + localX * sine + localY * cosine];
}

function lensMembranePath(ctx, cx, cy, radiusX, radiusY, rotation) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.moveTo(-radiusX, 0);
  ctx.bezierCurveTo(-radiusX * 0.52, -radiusY * 1.06, radiusX * 0.66, -radiusY * 0.82, radiusX, radiusY * 0.08);
  ctx.bezierCurveTo(radiusX * 0.54, radiusY * 0.94, -radiusX * 0.62, radiusY * 1.1, -radiusX, 0);
  ctx.closePath();
  ctx.restore();
}

function cubicBezierPoint(start, controlA, controlB, end, t) {
  const inverse = 1 - t;
  const inverse2 = inverse * inverse;
  const t2 = t * t;
  return [
    inverse2 * inverse * start[0] + 3 * inverse2 * t * controlA[0] + 3 * inverse * t2 * controlB[0] + t2 * t * end[0],
    inverse2 * inverse * start[1] + 3 * inverse2 * t * controlA[1] + 3 * inverse * t2 * controlB[1] + t2 * t * end[1],
  ];
}

function drawRefractiveCrescent(ctx, points, widthAt, palette, opacity, seed, foreground = false) {
  if (opacity <= 0.002 || points.length < 4) return;
  const corona = ribbonGeometry(points, widthAt, foreground ? 1.8 : 1.45);
  const body = ribbonGeometry(points, widthAt, 1);
  const interior = ribbonGeometry(points, widthAt, 0.48);
  const start = points[0];
  const end = points[points.length - 1];

  ctx.save();
  ctx.globalCompositeOperation = "screen";
  const coronaGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  coronaGradient.addColorStop(0, rgba(palette.blue, 0));
  coronaGradient.addColorStop(0.34, rgba(palette.cyan, opacity * 0.065));
  coronaGradient.addColorStop(0.7, rgba(palette.blue, opacity * 0.05));
  coronaGradient.addColorStop(1, rgba(palette.red, 0));
  ctx.fillStyle = coronaGradient;
  ctx.shadowColor = rgba(palette.cyan, opacity * 0.32);
  ctx.shadowBlur = foreground ? 10 : 6;
  traceRibbonPolygon(ctx, corona);
  ctx.fill();
  ctx.restore();

  ctx.save();
  const bodyGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  bodyGradient.addColorStop(0, rgba(palette.black, opacity * 0.86));
  bodyGradient.addColorStop(0.27, rgba(palette.gunmetal, opacity * 0.48));
  bodyGradient.addColorStop(0.5, rgba(palette.blue, opacity * 0.18));
  bodyGradient.addColorStop(0.73, rgba(palette.gunmetal, opacity * 0.44));
  bodyGradient.addColorStop(1, rgba(palette.black, opacity * 0.9));
  ctx.fillStyle = bodyGradient;
  traceRibbonPolygon(ctx, body);
  ctx.fill();

  const innerGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  innerGradient.addColorStop(0, rgba(palette.blue, 0));
  innerGradient.addColorStop(0.42, rgba(palette.cyan, opacity * 0.075));
  innerGradient.addColorStop(0.58, rgba(palette.white, opacity * 0.06));
  innerGradient.addColorStop(1, rgba(palette.red, 0));
  ctx.fillStyle = innerGradient;
  traceRibbonPolygon(ctx, interior);
  ctx.fill();

  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalCompositeOperation = "screen";
  ctx.strokeStyle = rgba(palette.cyan, opacity * (foreground ? 0.46 : 0.3));
  ctx.lineWidth = foreground ? 1.1 : 0.8;
  tracePointRail(ctx, body.left);
  ctx.stroke();
  ctx.strokeStyle = rgba(seed > 0.5 ? palette.red : palette.blue, opacity * (foreground ? 0.34 : 0.22));
  ctx.lineWidth = foreground ? 0.95 : 0.7;
  tracePointRail(ctx, body.right);
  ctx.stroke();

  // Longitudinal refraction rail and irregular caustic etching stay thin and subordinate.
  const rail = body.left.map(([x, y], index) => [
    mix(x, body.right[index][0], 0.36 + Math.sin(index * 0.47 + seed * 5) * 0.05),
    mix(y, body.right[index][1], 0.36 + Math.sin(index * 0.47 + seed * 5) * 0.05),
  ]);
  ctx.strokeStyle = rgba(palette.white, opacity * 0.13);
  ctx.lineWidth = 0.62;
  tracePointRail(ctx, rail);
  ctx.stroke();
  const step = Math.max(4, Math.round(points.length / 7));
  for (let index = step; index < points.length - 2; index += step) {
    const left = body.left[index];
    const right = body.right[index];
    const insetA = 0.14 + hash(Math.round(seed * 100000), index) * 0.13;
    const insetB = 0.72 + hash(Math.round(seed * 90000), index + 41) * 0.15;
    ctx.strokeStyle = rgba(index % (step * 2) ? palette.blue : palette.cyan, opacity * 0.18);
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(mix(left[0], right[0], insetA), mix(left[1], right[1], insetA));
    ctx.lineTo(mix(left[0], right[0], insetB), mix(left[1], right[1], insetB));
    ctx.stroke();
  }
  ctx.restore();
}

function drawBrokenGraviticCrown(ctx, width, height, scene, time, parallax, state) {
  const palette = scene.palette;
  const eventOpacity = clamp(Number(scene.source?.opacity ?? 1));
  const cx = width * 0.62 + parallax.x * -4;
  const cy = height * 0.43 + parallax.y * -2;
  const radiusX = Math.min(width * 0.158, height * 0.265);
  const radiusY = radiusX * 0.58;
  const rotation = -0.34;
  const energy = state.energy * eventOpacity;
  const active = clamp(state.charge * 0.48 + state.strike * 0.86 + state.response * 0.42);
  const animatedTime = time * state.motion;

  const project = (x, y) => {
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    return [cx + x * radiusX * cosine - y * radiusY * sine, cy + x * radiusX * sine + y * radiusY * cosine];
  };

  ctx.save();
  ctx.beginPath();
  ctx.rect(width * 0.405, height * 0.09, width * 0.44, height * 0.73);
  ctx.clip();

  // Light response is anchored to the station hub and planet-facing lower plane.
  const stationWash = ctx.createRadialGradient(cx - radiusX * 0.58, cy - radiusY * 0.2, 0, cx - radiusX * 0.58, cy - radiusY * 0.2, radiusX * 1.35);
  stationWash.addColorStop(0, rgba(palette.cyan, energy * 0.095));
  stationWash.addColorStop(0.35, rgba(palette.blue, energy * 0.04));
  stationWash.addColorStop(1, rgba(palette.cyan, 0));
  ctx.fillStyle = stationWash;
  ctx.fillRect(cx - radiusX * 1.9, cy - radiusY * 2.2, radiusX * 2.5, radiusY * 4);
  const planetResponse = ctx.createLinearGradient(cx, cy, cx + radiusX * 0.8, height * 0.8);
  planetResponse.addColorStop(0, rgba(palette.cyan, energy * 0.04));
  planetResponse.addColorStop(0.5, rgba(palette.blue, energy * 0.025));
  planetResponse.addColorStop(1, rgba(palette.cyan, 0));
  ctx.fillStyle = planetResponse;
  ctx.beginPath();
  ctx.moveTo(cx - radiusX * 0.3, cy);
  ctx.lineTo(cx + radiusX * 0.36, cy - radiusY * 0.08);
  ctx.lineTo(cx + radiusX * 0.95, height * 0.8);
  ctx.lineTo(cx - radiusX * 0.72, height * 0.8);
  ctx.closePath();
  ctx.fill();

  const curveDefinitions = [
    { p0: [-0.94, -0.2], p1: [-0.72, -0.84], p2: [0.12, -0.92], p3: [0.64, -0.34], width: 0.075, depth: 0.42 },
    { p0: [-0.86, 0.18], p1: [-0.4, -0.36], p2: [0.28, -0.18], p3: [0.88, -0.05], width: 0.055, depth: 0.58 },
    { p0: [-0.74, 0.42], p1: [-0.18, 0.78], p2: [0.42, 0.58], p3: [0.72, 0.18], width: 0.068, depth: 0.76 },
    { p0: [-0.32, -0.65], p1: [0.04, -0.25], p2: [0.34, 0.22], p3: [0.52, 0.63], width: 0.042, depth: 0.9 },
  ];
  const visibleCurves = state.phase === "idle" ? 1 : state.phase === "anticipation" ? 2 + Math.round(state.phaseT) : curveDefinitions.length;

  const buildCurve = (definition, curveIndex) => {
    const seed = hash(scene.seed ^ 0x71e9, curveIndex + 211);
    const p0 = project(definition.p0[0], definition.p0[1]);
    const p1 = project(definition.p1[0], definition.p1[1]);
    const p2 = project(definition.p2[0], definition.p2[1]);
    const p3 = project(definition.p3[0], definition.p3[1]);
    const points = [];
    const samples = state.density < 0.6 ? 18 : 34;
    for (let sample = 0; sample <= samples; sample += 1) {
      const t = sample / samples;
      const point = cubicBezierPoint(p0, p1, p2, p3, t);
      const irregularity = Math.sin(t * TAU * (3.2 + seed * 2.1) + curveIndex * 1.8 + animatedTime * 0.08) * radiusY * 0.012;
      point[0] += irregularity * Math.cos(rotation + Math.PI / 2);
      point[1] += irregularity * Math.sin(rotation + Math.PI / 2);
      points.push(point);
    }
    return { points, seed };
  };

  // Rear crescents disappear behind the lens membrane, establishing occlusion depth.
  for (let curveIndex = 0; curveIndex < Math.min(2, visibleCurves); curveIndex += 1) {
    const definition = curveDefinitions[curveIndex];
    const { points, seed } = buildCurve(definition, curveIndex);
    drawRefractiveCrescent(
      ctx,
      points,
      (t) => radiusX * definition.width * (0.5 + Math.sin(t * Math.PI) * (0.52 + active * 0.22)) * (0.88 + Math.sin(t * TAU * 3.1 + seed) * 0.12),
      palette,
      energy * definition.depth * 0.52,
      seed,
      false,
    );
  }

  ctx.save();
  lensMembranePath(ctx, cx, cy, radiusX * (0.78 + active * 0.055), radiusY * (0.8 + active * 0.04), rotation);
  ctx.clip();
  const membrane = ctx.createRadialGradient(cx - radiusX * 0.16, cy - radiusY * 0.12, radiusX * 0.025, cx, cy, radiusX * 0.84);
  membrane.addColorStop(0, rgba(palette.black, 0.97));
  membrane.addColorStop(0.42, rgba(palette.black, 0.93));
  membrane.addColorStop(0.76, rgba(palette.blue, energy * 0.16));
  membrane.addColorStop(1, rgba(palette.cyan, energy * 0.025));
  ctx.fillStyle = membrane;
  ctx.fillRect(cx - radiusX, cy - radiusY, radiusX * 2, radiusY * 2);

  // Internal shear bands are translucent refraction, not solid geometry.
  for (let shear = 0; shear < (state.density < 0.6 ? 3 : 7); shear += 1) {
    const seed = hash(scene.seed ^ 0x83a1, shear + 91);
    const y = cy + (shear - 3) * radiusY * 0.13;
    const drift = Math.sin(animatedTime * (0.17 + seed * 0.08) + shear * 1.53) * radiusX * 0.06;
    const gradient = ctx.createLinearGradient(cx - radiusX, y, cx + radiusX, y);
    gradient.addColorStop(0, rgba(palette.cyan, 0));
    gradient.addColorStop(0.31, rgba(palette.blue, energy * (0.032 + seed * 0.028)));
    gradient.addColorStop(0.53, rgba(palette.white, energy * 0.06));
    gradient.addColorStop(0.72, rgba(palette.red, energy * 0.014));
    gradient.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(cx - radiusX, y - radiusY * 0.018);
    ctx.bezierCurveTo(cx - radiusX * 0.28, y - radiusY * 0.11 + drift, cx + radiusX * 0.3, y + radiusY * 0.1 - drift, cx + radiusX, y + radiusY * 0.012);
    ctx.lineTo(cx + radiusX, y + radiusY * 0.045);
    ctx.bezierCurveTo(cx + radiusX * 0.28, y + radiusY * 0.14 - drift, cx - radiusX * 0.3, y - radiusY * 0.07 + drift, cx - radiusX, y + radiusY * 0.026);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Foreground crescents cross the membrane edge with brighter but restrained refraction.
  for (let curveIndex = 2; curveIndex < visibleCurves; curveIndex += 1) {
    const definition = curveDefinitions[curveIndex];
    const { points, seed } = buildCurve(definition, curveIndex);
    drawRefractiveCrescent(
      ctx,
      points,
      (t) => radiusX * definition.width * (0.46 + Math.sin(t * Math.PI) * (0.58 + active * 0.24)) * (0.88 + Math.sin(t * TAU * 2.7 + seed) * 0.12),
      palette,
      energy * definition.depth * 0.58,
      seed,
      true,
    );
  }

  // Two partial contour caustics define the lens without closing into a ring.
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.lineCap = "round";
  const contourA = [project(-0.86, -0.08), project(-0.7, -0.68), project(0.1, -0.78), project(0.65, -0.34)];
  const contourB = [project(-0.52, 0.46), project(-0.04, 0.72), project(0.43, 0.48), project(0.7, 0.16)];
  for (const [index, contour] of [contourA, contourB].entries()) {
    ctx.strokeStyle = rgba(index ? palette.red : palette.cyan, energy * (index ? 0.11 : 0.19));
    ctx.lineWidth = index ? 0.75 : 1.05;
    ctx.beginPath();
    ctx.moveTo(contour[0][0], contour[0][1]);
    ctx.bezierCurveTo(contour[1][0], contour[1][1], contour[2][0], contour[2][1], contour[3][0], contour[3][1]);
    ctx.stroke();
  }
  ctx.restore();

  // Fine slivers and dust follow lens tangents; no square or block particles are used.
  const debrisCount = Math.round((15 + active * 25 + state.response * 12) * state.density);
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  for (let debris = 0; debris < debrisCount; debris += 1) {
    const seed = hash(scene.seed ^ 0x44d1, debris + 501);
    const angle = seed * TAU + animatedTime * (0.035 + seed * 0.025);
    const radial = 0.72 + hash(scene.seed, debris + 709) * 0.58;
    const point = offAxisEllipsePoint(cx, cy, radiusX * radial, radiusY * radial, rotation, angle);
    const tangentX = -Math.sin(angle + rotation);
    const tangentY = Math.cos(angle + rotation);
    const length = 2 + seed * 8;
    const thickness = 0.35 + seed * 0.8;
    ctx.fillStyle = rgba(debris % 11 === 0 ? palette.red : debris % 4 === 0 ? palette.white : palette.cyan, energy * (0.035 + seed * 0.13));
    ctx.beginPath();
    ctx.moveTo(point[0] - tangentX * length, point[1] - tangentY * length);
    ctx.lineTo(point[0] + tangentX * length, point[1] + tangentY * length);
    ctx.lineTo(point[0] + tangentY * thickness, point[1] - tangentX * thickness);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  if (state.response > 0.02 || state.residue > 0.02) {
    const echo = clamp(state.response + state.residue * 0.38);
    const responseSheet = ctx.createLinearGradient(cx - radiusX * 1.55, cy, cx + radiusX * 0.1, cy);
    responseSheet.addColorStop(0, rgba(palette.cyan, 0));
    responseSheet.addColorStop(0.58, rgba(palette.blue, echo * 0.022));
    responseSheet.addColorStop(0.88, rgba(palette.cyan, echo * 0.055));
    responseSheet.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = responseSheet;
    ctx.beginPath();
    ctx.moveTo(cx - radiusX * 1.55, cy - radiusY * 0.15);
    ctx.bezierCurveTo(cx - radiusX * 0.92, cy - radiusY * 0.52, cx - radiusX * 0.26, cy - radiusY * 0.28, cx + radiusX * 0.1, cy);
    ctx.bezierCurveTo(cx - radiusX * 0.3, cy + radiusY * 0.25, cx - radiusX * 0.98, cy + radiusY * 0.44, cx - radiusX * 1.55, cy + radiusY * 0.14);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function drawFilledPlasmaLobe(ctx, points, widthAt, colors, opacity, options = {}) {
  if (opacity <= 0.002 || points.length < 4) return;
  const corona = ribbonGeometry(points, widthAt, Number(options.coronaScale) || 2.15);
  const body = ribbonGeometry(points, widthAt, 1);
  const mantle = ribbonGeometry(points, widthAt, Number(options.mantleScale) || 0.62);
  const start = points[0];
  const end = points[points.length - 1];
  const blur = Number(options.blur) || 18;
  const seed = Number(options.seed) || 0;

  // A broad, soft emission volume surrounds the filled body. There is deliberately no
  // longitudinal outline or bright rail: the silhouette must read as plasma, not string.
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.shadowColor = rgba(colors.body, opacity * 0.46);
  ctx.shadowBlur = blur;
  const coronaGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  coronaGradient.addColorStop(0, rgba(colors.edge, 0));
  coronaGradient.addColorStop(0.22, rgba(colors.edge, opacity * 0.055));
  coronaGradient.addColorStop(0.58, rgba(colors.body, opacity * 0.11));
  coronaGradient.addColorStop(0.84, rgba(colors.edge, opacity * 0.052));
  coronaGradient.addColorStop(1, rgba(colors.edge, 0));
  ctx.fillStyle = coronaGradient;
  traceRibbonPolygon(ctx, corona);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalCompositeOperation = "screen";
  const bodyGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  bodyGradient.addColorStop(0, rgba(colors.edge, opacity * 0.08));
  bodyGradient.addColorStop(0.18, rgba(colors.edge, opacity * 0.32));
  bodyGradient.addColorStop(0.43, rgba(colors.body, opacity * 0.66));
  bodyGradient.addColorStop(0.68, rgba(colors.hot, opacity * 0.48));
  bodyGradient.addColorStop(0.88, rgba(colors.body, opacity * 0.38));
  bodyGradient.addColorStop(1, rgba(colors.edge, opacity * 0.04));
  ctx.fillStyle = bodyGradient;
  ctx.shadowColor = rgba(colors.body, opacity * 0.38);
  ctx.shadowBlur = blur * 0.34;
  traceRibbonPolygon(ctx, body);
  ctx.fill();

  const mantleGradient = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  mantleGradient.addColorStop(0, rgba(colors.body, 0));
  mantleGradient.addColorStop(0.32, rgba(colors.hot, opacity * 0.16));
  mantleGradient.addColorStop(0.6, rgba(colors.hot, opacity * (Number(options.hotStrength) || 0.27)));
  mantleGradient.addColorStop(0.82, rgba(colors.body, opacity * 0.12));
  mantleGradient.addColorStop(1, rgba(colors.body, 0));
  ctx.fillStyle = mantleGradient;
  ctx.shadowBlur = blur * 0.18;
  traceRibbonPolygon(ctx, mantle);
  ctx.fill();
  ctx.restore();

  // Interior hot pockets and cooled cavities create turbulent material breakup. They are
  // clipped inside the volume and never become an exposed wireframe or center line.
  ctx.save();
  traceRibbonPolygon(ctx, body);
  ctx.clip();
  const pocketCount = Number(options.pocketCount) || 7;
  for (let pocket = 0; pocket < pocketCount; pocket += 1) {
    const random = hash(Math.round(seed * 100003) ^ points.length, pocket + 71);
    const t = 0.12 + random * 0.77;
    const index = Math.min(points.length - 1, Math.max(0, Math.round(t * (points.length - 1))));
    const point = points[index];
    const localWidth = Math.max(2, widthAt(t, index));
    const offset = (hash(Math.round(seed * 81013), pocket + 211) - 0.5) * localWidth * 0.95;
    const radius = localWidth * (0.42 + hash(Math.round(seed * 63127), pocket + 359) * 0.72);
    if (pocket % 3 === 1) {
      const cavity = ctx.createRadialGradient(point[0] + offset, point[1], 0, point[0] + offset, point[1], radius * 1.55);
      cavity.addColorStop(0, rgba(colors.shadow, opacity * 0.34));
      cavity.addColorStop(0.48, rgba(colors.shadow, opacity * 0.16));
      cavity.addColorStop(1, rgba(colors.shadow, 0));
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = cavity;
      ctx.fillRect(point[0] + offset - radius * 1.7, point[1] - radius * 1.7, radius * 3.4, radius * 3.4);
    } else {
      const pocketGlow = ctx.createRadialGradient(point[0] + offset, point[1], 0, point[0] + offset, point[1], radius);
      pocketGlow.addColorStop(0, rgba(colors.hot, opacity * 0.17));
      pocketGlow.addColorStop(0.35, rgba(colors.body, opacity * 0.11));
      pocketGlow.addColorStop(1, rgba(colors.body, 0));
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = pocketGlow;
      ctx.fillRect(point[0] + offset - radius, point[1] - radius, radius * 2, radius * 2);
    }
  }
  ctx.restore();
}

function drawVolumetricSmokePlume(ctx, points, widthAt, palette, opacity, seed) {
  if (opacity <= 0.002 || points.length < 4) return;
  const volume = ribbonGeometry(points, widthAt, 1);
  const start = points[0];
  const end = points[points.length - 1];
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.filter = "blur(5px)";
  const smoke = ctx.createLinearGradient(start[0], start[1], end[0], end[1]);
  smoke.addColorStop(0, rgba(palette.black, 0));
  smoke.addColorStop(0.18, rgba(palette.red, opacity * 0.1));
  smoke.addColorStop(0.5, rgba(palette.gunmetal, opacity * (0.24 + seed * 0.12)));
  smoke.addColorStop(0.78, rgba(palette.black, opacity * 0.38));
  smoke.addColorStop(1, rgba(palette.black, 0));
  ctx.fillStyle = smoke;
  traceRibbonPolygon(ctx, volume);
  ctx.fill();
  ctx.filter = "none";
  ctx.restore();
}

function drawFoundryLifecycle(ctx, width, height, scene, time, parallax, state) {
  const palette = scene.palette;
  const ember = palette.ember || "#ff9a3c";
  const molten = "#ffbd68";
  const furnaceHot = "#ffe0a0";
  const eventOpacity = clamp(Number(scene.source?.opacity ?? 1));
  const cx = width * 0.425 + parallax.x * 3;
  const topY = height * 0.17 + parallax.y;
  const bottomY = height * 0.79 + parallax.y * 1.4;
  const coreY = mix(topY, bottomY, 0.59);
  const energy = state.energy * eventOpacity;
  const active = clamp(state.charge * 0.5 + state.strike * 0.88 + state.response * 0.4);
  const animatedTime = time * state.motion;
  const fieldRadius = height * (0.032 + active * 0.074);

  const buildLobe = (lobeIndex, definition) => {
    const seed = hash(scene.seed ^ 0xb8a1d, lobeIndex + 181);
    const samples = state.density < 0.6 ? 20 : 38;
    const points = [];
    const widths = [];
    for (let sample = 0; sample <= samples; sample += 1) {
      const localT = sample / samples;
      const t = mix(definition.startT, definition.endT, localT);
      const envelope = Math.sin(localT * Math.PI) ** (0.48 + seed * 0.32);
      const slowFold = Math.sin(t * TAU * (0.72 + seed * 0.46) + definition.phase + animatedTime * (0.13 + seed * 0.08));
      const turbulentFold = Math.sin(t * TAU * (2.4 + seed * 1.9) + definition.phase * 1.7 - animatedTime * (0.18 + seed * 0.11));
      const fineFold = Math.sin(t * TAU * (6.1 + seed * 3.2) + lobeIndex * 2.17) * (0.2 + active * 0.3);
      const lateral = definition.offset * envelope + fieldRadius * envelope * (slowFold * definition.fold + turbulentFold * definition.turbulence + fineFold * 0.045);
      const rise = Math.sin(localT * TAU * (1.7 + seed) + definition.phase) * height * 0.0035 * envelope;
      points.push([cx + lateral + (t - 0.5) * fieldRadius * definition.lean, mix(topY, bottomY, t) + rise]);
      const breakup = 0.78 + Math.sin(localT * TAU * (3.3 + seed * 2.4) + definition.phase) * 0.16 + hash(scene.seed ^ lobeIndex, sample + 503) * 0.1;
      widths.push(fieldRadius * definition.width * envelope * breakup);
    }
    return { points, widths, seed };
  };

  ctx.save();
  ctx.beginPath();
  ctx.rect(width * 0.31, height * 0.055, width * 0.275, height * 0.88);
  ctx.clip();

  // The event throws light onto the furnace bay and onto the floor plane beneath it.
  const furnaceLight = ctx.createRadialGradient(cx, coreY, 0, cx, coreY, height * (0.16 + active * 0.27));
  furnaceLight.addColorStop(0, rgba(furnaceHot, energy * 0.15));
  furnaceLight.addColorStop(0.1, rgba(ember, energy * 0.23));
  furnaceLight.addColorStop(0.34, rgba(palette.red, energy * 0.13));
  furnaceLight.addColorStop(0.68, rgba(palette.red, energy * 0.042));
  furnaceLight.addColorStop(1, rgba(palette.red, 0));
  ctx.fillStyle = furnaceLight;
  ctx.fillRect(width * 0.3, height * 0.06, width * 0.3, height * 0.84);

  const floorReflection = ctx.createLinearGradient(cx, height * 0.63, cx, height * 0.96);
  floorReflection.addColorStop(0, rgba(ember, 0));
  floorReflection.addColorStop(0.28, rgba(ember, energy * 0.1));
  floorReflection.addColorStop(0.58, rgba(palette.red, energy * 0.055));
  floorReflection.addColorStop(1, rgba(palette.red, 0));
  ctx.fillStyle = floorReflection;
  ctx.beginPath();
  ctx.moveTo(cx - fieldRadius * 0.52, height * 0.62);
  ctx.lineTo(cx + fieldRadius * 0.64, height * 0.62);
  ctx.lineTo(cx + fieldRadius * 1.95, height * 0.95);
  ctx.lineTo(cx - fieldRadius * 1.66, height * 0.95);
  ctx.closePath();
  ctx.fill();

  // Smoke is broad, layered and materially dark. It establishes depth behind the emission.
  const smokeCount = state.density < 0.6 ? 2 : 5;
  for (let plume = 0; plume < smokeCount; plume += 1) {
    const seed = hash(scene.seed ^ 0x4ea7, plume + 61);
    const samples = state.density < 0.6 ? 16 : 28;
    const points = [];
    const widths = [];
    const startX = cx + (seed - 0.5) * fieldRadius * 1.4;
    for (let sample = 0; sample <= samples; sample += 1) {
      const t = sample / samples;
      const envelope = Math.sin(t * Math.PI) ** 0.56;
      const sway = Math.sin(t * TAU * (0.8 + seed * 0.55) + plume * 1.8 + animatedTime * (0.1 + seed * 0.06)) * fieldRadius * (0.28 + seed * 0.32) * envelope;
      const y = mix(bottomY - height * (0.05 + seed * 0.08), topY - height * (0.09 + seed * 0.08), t);
      points.push([startX + sway + (t - 0.35) * fieldRadius * (seed - 0.5) * 0.55, y]);
      widths.push(fieldRadius * (0.22 + seed * 0.18) * envelope * (0.84 + Math.sin(t * TAU * 3.6 + seed * 9) * 0.16));
    }
    drawVolumetricSmokePlume(ctx, points, (_t, index) => widths[index], palette, energy * (0.2 + seed * 0.2), seed);
  }

  // Independent filled tongues form an irregular plasma mass. Their uneven lengths,
  // folds and widths prevent the symmetric double-helix appearance of the prior version.
  const definitions = [
    { startT: 0.03, endT: 0.98, offset: -0.42, fold: 0.28, turbulence: 0.13, width: 0.32, lean: -0.12, phase: 0.3 },
    { startT: 0.09, endT: 0.92, offset: 0.34, fold: 0.24, turbulence: 0.18, width: 0.28, lean: 0.14, phase: 2.1 },
    { startT: 0.22, endT: 1, offset: -0.05, fold: 0.18, turbulence: 0.2, width: 0.38, lean: 0.05, phase: 4.2 },
    { startT: 0, endT: 0.72, offset: 0.56, fold: 0.17, turbulence: 0.16, width: 0.22, lean: -0.08, phase: 5.4 },
    { startT: 0.3, endT: 0.84, offset: -0.62, fold: 0.15, turbulence: 0.2, width: 0.2, lean: 0.18, phase: 6.7 },
  ];
  const visibleLobes = state.phase === "idle" ? 2 : state.phase === "anticipation" ? 3 + Math.round(state.phaseT) : definitions.length;
  const lobeCache = [];
  for (let lobe = 0; lobe < visibleLobes; lobe += 1) {
    const definition = definitions[lobe];
    const geometry = buildLobe(lobe, definition);
    lobeCache.push(geometry);
    const cyanPhaseEdge = lobe === 3;
    drawFilledPlasmaLobe(
      ctx,
      geometry.points,
      (_t, index) => geometry.widths[index],
      cyanPhaseEdge
        ? { edge: palette.blue, body: palette.cyan, hot: molten, shadow: palette.black }
        : { edge: palette.red, body: lobe === 2 ? molten : ember, hot: furnaceHot, shadow: palette.black },
      energy * (cyanPhaseEdge ? 0.2 + active * 0.08 : 0.34 + active * 0.18),
      { seed: geometry.seed, blur: 13 + active * 11, coronaScale: 2.05 + lobe * 0.09, mantleScale: 0.58, hotStrength: cyanPhaseEdge ? 0.1 : 0.24, pocketCount: 6 },
    );
  }

  // Narrow dark channels cut through the filled mass. They preserve negative space and
  // keep peak activation from collapsing into one white column.
  const gapDefinitions = [
    { startT: 0.12, endT: 0.92, offset: -0.14, fold: 0.2, turbulence: 0.12, width: 0.075, lean: 0.1, phase: 1.4 },
    { startT: 0.28, endT: 0.82, offset: 0.22, fold: 0.18, turbulence: 0.11, width: 0.062, lean: -0.13, phase: 4.8 },
  ];
  for (let gap = 0; gap < gapDefinitions.length; gap += 1) {
    const geometry = buildLobe(gap + 20, gapDefinitions[gap]);
    const gapBody = ribbonGeometry(geometry.points, (_t, index) => geometry.widths[index], 1);
    const gapGradient = ctx.createLinearGradient(cx, topY, cx, bottomY);
    gapGradient.addColorStop(0, rgba(palette.black, 0));
    gapGradient.addColorStop(0.24, rgba(palette.black, energy * 0.42));
    gapGradient.addColorStop(0.7, rgba(palette.black, energy * 0.58));
    gapGradient.addColorStop(1, rgba(palette.black, 0));
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = gapGradient;
    traceRibbonPolygon(ctx, gapBody);
    ctx.fill();
  }

  // Two compact hot bodies sit inside the larger plasma instead of tracing its length.
  const hotDefinitions = [
    { startT: 0.32, endT: 0.7, offset: -0.2, fold: 0.1, turbulence: 0.09, width: 0.14, lean: 0.04, phase: 0.9 },
    { startT: 0.44, endT: 0.79, offset: 0.18, fold: 0.11, turbulence: 0.08, width: 0.12, lean: -0.03, phase: 3.7 },
  ];
  for (let hot = 0; hot < hotDefinitions.length; hot += 1) {
    const geometry = buildLobe(hot + 30, hotDefinitions[hot]);
    drawFilledPlasmaLobe(
      ctx,
      geometry.points,
      (_t, index) => geometry.widths[index],
      { edge: palette.red, body: molten, hot: furnaceHot, shadow: palette.black },
      energy * (0.22 + active * 0.2),
      { seed: geometry.seed, blur: 8 + active * 5, coronaScale: 1.48, mantleScale: 0.7, hotStrength: 0.2, pocketCount: 4 },
    );
  }

  // Heat-haze sheets bend independently of the flame body and fade before the copy area.
  for (let haze = 0; haze < (state.density < 0.6 ? 1 : 4); haze += 1) {
    const seed = hash(scene.seed ^ 0x671d, haze + 111);
    const baseX = cx + (seed - 0.5) * fieldRadius * 1.3;
    const sway = Math.sin(animatedTime * (0.16 + seed * 0.09) + haze * 2.2) * fieldRadius * 0.5;
    const hazeGradient = ctx.createLinearGradient(baseX, coreY, baseX, topY - height * 0.12);
    hazeGradient.addColorStop(0, rgba(ember, energy * 0.02));
    hazeGradient.addColorStop(0.46, rgba(furnaceHot, energy * 0.014));
    hazeGradient.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = hazeGradient;
    ctx.beginPath();
    ctx.moveTo(baseX - fieldRadius * 0.18, coreY + height * 0.04);
    ctx.bezierCurveTo(baseX + sway, coreY - height * 0.12, baseX - sway * 0.8, topY, baseX + fieldRadius * 0.04, topY - height * 0.12);
    ctx.bezierCurveTo(baseX + fieldRadius * 0.24, topY, baseX + sway * 0.3, coreY - height * 0.1, baseX + fieldRadius * 0.17, coreY + height * 0.04);
    ctx.closePath();
    ctx.fill();
  }

  // Sparse electrical contacts terminate on actual machinery and remain subordinate.
  const machinery = [[width * 0.337, height * 0.24], [width * 0.356, height * 0.78], [width * 0.562, height * 0.29], [width * 0.55, height * 0.69]];
  const arcCount = Math.round((1 + active * 3 + state.cue) * state.density);
  const sourceLobe = lobeCache[Math.min(2, Math.max(0, lobeCache.length - 1))];
  if (sourceLobe) {
    for (let arc = 0; arc < arcCount; arc += 1) {
      const seed = hash(scene.seed ^ 0xa2c9, arc + 301);
      const target = machinery[arc % machinery.length];
      const sourceIndex = Math.min(sourceLobe.points.length - 1, Math.round((0.24 + seed * 0.54) * (sourceLobe.points.length - 1)));
      const source = sourceLobe.points[sourceIndex];
      const direction = target[0] < cx ? -1 : 1;
      const path = () => {
        ctx.beginPath();
        ctx.moveTo(source[0], source[1]);
        ctx.lineTo(mix(source[0], target[0], 0.35), mix(source[1], target[1], 0.35) + direction * height * (0.009 + seed * 0.011));
        ctx.lineTo(mix(source[0], target[0], 0.68), mix(source[1], target[1], 0.68) - direction * height * (0.009 + seed * 0.008));
        ctx.lineTo(target[0], target[1]);
      };
      strokeLayeredEnergy(ctx, path, arc === 2 ? palette.cyan : ember, furnaceHot, Math.max(0.55, height * 0.0008), energy * active * (0.11 + seed * 0.13), 6);
      const contact = ctx.createRadialGradient(target[0], target[1], 0, target[0], target[1], height * 0.013);
      contact.addColorStop(0, rgba(furnaceHot, energy * active * 0.28));
      contact.addColorStop(0.2, rgba(arc === 2 ? palette.cyan : ember, energy * active * 0.2));
      contact.addColorStop(1, rgba(ember, 0));
      ctx.fillStyle = contact;
      ctx.fillRect(target[0] - height * 0.013, target[1] - height * 0.013, height * 0.026, height * 0.026);
    }
  }

  // Incandescent embers have cooling trails, ballistic falloff and floor contact.
  const sparkCount = Math.round((11 + active * 38 + state.response * 18 + state.cue * 9) * state.density);
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "round";
  for (let spark = 0; spark < sparkCount; spark += 1) {
    const seed = hash(scene.seed ^ 0x5f4a, spark + 521);
    const age = (state.localTime * (0.27 + seed * 0.24) + hash(scene.seed, spark + 853)) % 1;
    const direction = hash(scene.seed, spark + 1009) > 0.46 ? 1 : -1;
    const speed = height * (0.1 + seed * 0.21) * (0.48 + active * 0.74);
    const x = cx + direction * speed * age * (0.35 + seed * 0.68) + Math.sin(age * TAU * 2.1 + seed * 9) * height * 0.006;
    const y = coreY - speed * age * (0.58 + seed * 0.68) + height * 0.31 * age * age;
    const tail = height * (0.005 + seed * 0.014) * (1 - age);
    const alpha = energy * Math.sin(age * Math.PI) * (0.17 + seed * 0.48);
    const sparkColor = seed > 0.9 ? palette.cyan : seed > 0.32 ? ember : furnaceHot;
    ctx.shadowColor = rgba(sparkColor, alpha * 0.7);
    ctx.shadowBlur = 2 + seed * 5;
    ctx.strokeStyle = rgba(sparkColor, alpha);
    ctx.lineWidth = 0.55 + seed * 1.05;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - direction * tail, y + tail * (0.38 + seed * 0.58));
    ctx.stroke();
  }
  ctx.restore();

  if (state.phase === "decay") {
    const cooling = ctx.createLinearGradient(cx - fieldRadius * 2.4, coreY, cx + fieldRadius * 2.4, coreY);
    cooling.addColorStop(0, rgba(palette.cyan, 0));
    cooling.addColorStop(0.42, rgba(palette.blue, state.residue * 0.022));
    cooling.addColorStop(0.5, rgba(palette.cyan, state.residue * 0.04));
    cooling.addColorStop(0.62, rgba(palette.blue, state.residue * 0.02));
    cooling.addColorStop(1, rgba(palette.cyan, 0));
    ctx.fillStyle = cooling;
    ctx.fillRect(cx - fieldRadius * 2.5, topY, fieldRadius * 5, bottomY - topY);
  }
  ctx.restore();
}

function drawCinematicLifecycleEvent(ctx, width, height, scene, conceptId, time, parallax, cueStrength, reducedMotion) {
  if (scene.source?.enabled === false || scene.cinematicEvent?.enabled === false) return;
  const state = cinematicLifecycle(time, cueStrength, reducedMotion);
  if (conceptId === "orbital-commons") {
    drawBrokenGraviticCrown(ctx, width, height, scene, time, parallax, state);
  } else if (conceptId === "phase-foundry") {
    drawFoundryLifecycle(ctx, width, height, scene, time, parallax, state);
  } else {
    drawGhostlineLifecycle(ctx, width, height, scene, time, parallax, state);
  }
}

function drawAtmosphere(ctx, width, height, data, scene, time, parallax, mode) {
  const layer = scene.decayAtmosphere;
  const palette = scene.palette;
  const color = mode === "foundry" ? palette.red : mode === "space" ? palette.white : palette.cyan;
  for (const mote of data.dust) {
    const x = ((mote.x + time * mote.speed * mote.drift * layer.speed + 1) % 1) * width + parallax.x * mote.depth * 3;
    const y = ((mote.y - time * mote.speed * layer.speed + 1) % 1) * height + parallax.y * mote.depth * 2;
    const breathe = 0.45 + 0.55 * Math.sin(time * 0.6 + mote.phase) ** 2;
    ctx.fillStyle = rgba(color, layer.opacity * mote.depth * breathe * 0.18);
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.35, mote.depth * 1.25), 0, TAU);
    ctx.fill();
  }

  if (mode !== "space") {
    const fogHeight = height * 0.38;
    for (let index = 0; index < 5; index += 1) {
      const x = width * (((index * 0.23 + time * layer.speed * 0.009) % 1) - 0.12);
      const y = height * (0.55 + index * 0.065);
      const fog = ctx.createRadialGradient(x, y, 0, x, y, width * 0.26);
      fog.addColorStop(0, rgba(mode === "foundry" ? palette.red : palette.cyan, layer.opacity * 0.024));
      fog.addColorStop(1, rgba(palette.black, 0));
      ctx.fillStyle = fog;
      ctx.fillRect(x - width * 0.26, y - fogHeight * 0.5, width * 0.52, fogHeight);
    }
  }
}

function drawLegibilityVeil(ctx, width, height, scene) {
  const config = scene.legibility || {};
  if (config.enabled === false || Number(config.opacity) <= 0) return;
  const x = width * (Number(config.x) || 0);
  const y = height * (Number(config.y) || 0);
  const regionWidth = width * (Number(config.width) || 0.4);
  const regionHeight = height * (Number(config.height) || 0.84);
  const centerX = x + regionWidth / 2;
  const centerY = y + regionHeight / 2;
  const radius = Math.max(regionWidth, regionHeight) * 0.72;
  const veil = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
  const opacity = clamp(Number(config.opacity) || 0.7);
  veil.addColorStop(0, rgba(scene.palette.black, opacity * 0.72));
  veil.addColorStop(0.58, rgba(scene.palette.black, opacity * 0.48));
  veil.addColorStop(1, rgba(scene.palette.black, 0));
  ctx.fillStyle = veil;
  ctx.fillRect(x - regionWidth * 0.32, y - regionHeight * 0.2, regionWidth * 1.64, regionHeight * 1.4);
}

function drawEdgeGrade(ctx, width, height, scene) {
  const edge = ctx.createRadialGradient(width * 0.5, height * 0.47, Math.min(width, height) * 0.2, width * 0.5, height * 0.5, Math.max(width, height) * 0.72);
  edge.addColorStop(0, rgba(scene.palette.black, 0));
  edge.addColorStop(0.74, rgba(scene.palette.black, 0.08));
  edge.addColorStop(1, rgba(scene.palette.black, 0.72));
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, width, height);
}

function cueEnvelope(cueState, time) {
  if (!cueState.type) return 0;
  const age = time - cueState.startedAt;
  if (age < 0 || age > cueState.duration) return 0;
  const normalized = age / cueState.duration;
  const attack = smoothstep(0, 0.16, normalized);
  const decay = 1 - smoothstep(0.38, 1, normalized);
  return attack * decay;
}

function qualityScaleFor(scene, reducedMotion) {
  const mode = String(scene.quality?.mode || "high").toLowerCase();
  const modeScale = mode === "low" ? 0.46 : mode === "balanced" || mode === "medium" ? 0.72 : 1;
  const detail = clamp(Number(scene.quality?.detail ?? 1), 0.25, 1.5);
  const density = clamp(
    (Number(scene.secondaryMotion?.density ?? 1) + Number(scene.decayAtmosphere?.density ?? 1)) / 2,
    0.35,
    1.2,
  );
  return modeScale * detail * density * (reducedMotion ? 0.64 : 1);
}

/**
 * Deterministic Canvas 2D/2.5D living-background compositor.
 *
 * Each concept accepts editable `concept.scene` controls for its source/core,
 * primary environment, secondary motion, environmental response, atmosphere,
 * quality, palette and form-legibility veil.
 */
export function createLivingCompositor(
  canvas,
  { concept = "ghostline", reducedMotion = false, still = false, onStats } = {},
) {
  if (!canvas || typeof canvas.getContext !== "function") {
    throw new TypeError("createLivingCompositor requires a canvas element");
  }
  const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
  if (!ctx) throw new Error("Canvas 2D is unavailable");

  let resolved = resolveScene(concept);
  const captureTimeOverride = queryCaptureTimeOverride();
  let mediaQuery = null;
  let systemReducedMotion = false;
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    systemReducedMotion = mediaQuery.matches;
  }
  let reduceMotion = Boolean(reducedMotion || systemReducedMotion);
  const stillFrame = Boolean(still || queryRequestsStillFrame());
  if (stillFrame && captureTimeOverride !== null) resolved.scene.stillTime = captureTimeOverride;
  let qualityScale = qualityScaleFor(resolved.scene, reduceMotion);
  let sceneData = makeSceneData(resolved.id, resolved.scene, qualityScale);
  let staticCanvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
  let staticCtx = staticCanvas?.getContext("2d", { alpha: false }) || null;
  let logicalWidth = 1;
  let logicalHeight = 1;
  let dpr = 1;
  let animationFrame = 0;
  let startTimestamp = null;
  let elapsedOffset = 0;
  let lastTimestamp = 0;
  let lastStatsTimestamp = 0;
  let destroyed = false;
  let visible = typeof document === "undefined" || document.visibilityState !== "hidden";
  let manuallyPaused = false;
  let pointer = { x: 0, y: 0 };
  let pointerTarget = { x: 0, y: 0 };
  let cueState = { type: null, startedAt: -100, duration: 1.1 };
  let resizeObserver = null;
  const stats = {
    concept: resolved.id,
    width: 1,
    height: 1,
    dpr: 1,
    quality: resolved.scene.quality.mode,
    reducedMotion: reduceMotion,
    still: stillFrame,
    paused: false,
    frameCount: 0,
    fps: 0,
    entities: 0,
    generatedEntities: 0,
    entityBreakdown: null,
    activeCue: null,
    captureTime: stillFrame ? Number(resolved.scene.stillTime) || 0 : null,
  };

  function syncEntityStats() {
    const breakdown = renderedEntityBreakdown(resolved.id, sceneData, resolved.scene);
    stats.entities = breakdown.total;
    stats.generatedEntities = generatedEntityCount(sceneData);
    stats.entityBreakdown = breakdown;
  }

  function rebuildStaticLayer() {
    if (!staticCanvas || !staticCtx) return;
    staticCanvas.width = Math.max(1, Math.round(logicalWidth * dpr));
    staticCanvas.height = Math.max(1, Math.round(logicalHeight * dpr));
    staticCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    staticCtx.clearRect(0, 0, logicalWidth, logicalHeight);
    if (resolved.id === "orbital-commons") {
      drawOrbitalStatic(staticCtx, logicalWidth, logicalHeight, sceneData, resolved.scene);
    } else if (resolved.id === "phase-foundry") {
      drawFoundryStatic(staticCtx, logicalWidth, logicalHeight, sceneData, resolved.scene);
    } else {
      drawGhostlineStatic(staticCtx, logicalWidth, logicalHeight, sceneData, resolved.scene);
    }
  }

  function resize() {
    if (destroyed) return getStats();
    const rect = typeof canvas.getBoundingClientRect === "function" ? canvas.getBoundingClientRect() : null;
    logicalWidth = Math.max(1, Math.round(rect?.width || canvas.clientWidth || canvas.width || 1));
    logicalHeight = Math.max(1, Math.round(rect?.height || canvas.clientHeight || canvas.height || 1));
    const requestedCap = clamp(Number(resolved.scene.quality?.dprCap || 1.65), 1, 2);
    const deviceDpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    dpr = Math.min(requestedCap, reduceMotion ? 1.25 : deviceDpr);
    const pixelWidth = Math.max(1, Math.round(logicalWidth * dpr));
    const pixelHeight = Math.max(1, Math.round(logicalHeight * dpr));
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    stats.width = logicalWidth;
    stats.height = logicalHeight;
    stats.dpr = dpr;
    rebuildStaticLayer();
    render(stillFrame ? Number(resolved.scene.stillTime) || 0 : elapsedTime(lastTimestamp || 0));
    return getStats();
  }

  function elapsedTime(timestamp) {
    if (stillFrame) return Number(resolved.scene.stillTime) || 0;
    if (manuallyPaused) return elapsedOffset;
    if (startTimestamp === null) startTimestamp = timestamp;
    const multiplier = reduceMotion ? 0.16 : 1;
    return elapsedOffset + Math.max(0, (timestamp - startTimestamp) / 1000) * multiplier;
  }

  function render(time) {
    if (destroyed || !logicalWidth || !logicalHeight) return;
    pointer.x += (pointerTarget.x - pointer.x) * (reduceMotion ? 0.025 : 0.075);
    pointer.y += (pointerTarget.y - pointer.y) * (reduceMotion ? 0.025 : 0.075);
    const parallaxAmount = clamp(Number(resolved.scene.motion?.parallax ?? 1), 0, 1.6);
    const parallax = stillFrame ? { x: 0, y: 0 } : { x: pointer.x * parallaxAmount, y: pointer.y * parallaxAmount };
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, logicalWidth, logicalHeight);
    ctx.fillStyle = resolved.scene.palette.void;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);
    if (staticCanvas) {
      const shiftX = parallax.x * 3;
      const shiftY = parallax.y * 2;
      ctx.drawImage(staticCanvas, shiftX, shiftY, logicalWidth, logicalHeight);
    }
    const cueStrength = cueEnvelope(cueState, time);
    if (resolved.id === "orbital-commons") {
      drawOrbital(ctx, logicalWidth, logicalHeight, sceneData, resolved.scene, time, parallax, cueStrength);
    } else if (resolved.id === "phase-foundry") {
      drawFoundry(ctx, logicalWidth, logicalHeight, sceneData, resolved.scene, time, parallax, cueStrength);
    } else {
      drawGhostline(ctx, logicalWidth, logicalHeight, sceneData, resolved.scene, time, parallax, cueStrength);
    }
    drawCinematicLifecycleEvent(ctx, logicalWidth, logicalHeight, resolved.scene, resolved.id, time, parallax, cueStrength, reduceMotion);
    drawLegibilityVeil(ctx, logicalWidth, logicalHeight, resolved.scene);
    drawEdgeGrade(ctx, logicalWidth, logicalHeight, resolved.scene);
    stats.frameCount += 1;
    stats.activeCue = cueStrength > 0 ? cueState.type : null;
  }

  function animate(timestamp) {
    if (destroyed) return;
    const targetFps = clamp(Number(resolved.scene.quality?.fps || 30), 12, 60);
    const interval = 1000 / (reduceMotion ? Math.min(15, targetFps) : targetFps);
    if (visible && !manuallyPaused && timestamp - lastTimestamp >= interval - 1) {
      const previous = lastTimestamp;
      lastTimestamp = timestamp;
      render(elapsedTime(timestamp));
      stats.fps = previous ? Math.round(1000 / Math.max(1, timestamp - previous)) : targetFps;
      if (timestamp - lastStatsTimestamp >= 1000) {
        lastStatsTimestamp = timestamp;
        if (typeof onStats === "function") {
          try {
            onStats(getStats());
          } catch {
            // Stats callbacks must never interrupt the visual loop.
          }
        }
      }
    }
    animationFrame = requestAnimationFrame(animate);
  }

  function setConcept(nextConcept) {
    resolved = resolveScene(nextConcept);
    if (stillFrame && captureTimeOverride !== null) resolved.scene.stillTime = captureTimeOverride;
    qualityScale = qualityScaleFor(resolved.scene, reduceMotion);
    sceneData = makeSceneData(resolved.id, resolved.scene, qualityScale);
    stats.concept = resolved.id;
    stats.quality = resolved.scene.quality.mode;
    syncEntityStats();
    elapsedOffset = 0;
    startTimestamp = lastTimestamp || (typeof performance !== "undefined" ? performance.now() : 0);
    cueState = { type: "concept", startedAt: 0, duration: 1.25 };
    rebuildStaticLayer();
    render(stillFrame ? Number(resolved.scene.stillTime) || 0 : 0);
    return getStats();
  }

  function setPointer(x, y) {
    if (stillFrame || manuallyPaused) return;
    let normalizedX = Number(x) || 0;
    let normalizedY = Number(y) || 0;
    if (Math.abs(normalizedX) > 1 || Math.abs(normalizedY) > 1) {
      const rect = canvas.getBoundingClientRect();
      normalizedX = ((normalizedX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
      normalizedY = ((normalizedY - rect.top) / Math.max(1, rect.height)) * 2 - 1;
    }
    pointerTarget.x = clamp(normalizedX, -1, 1);
    pointerTarget.y = clamp(normalizedY, -1, 1);
  }

  function cue(type) {
    if (manuallyPaused) return;
    const now = lastTimestamp || (typeof performance !== "undefined" ? performance.now() : 0);
    cueState = {
      type: String(type || "pulse"),
      startedAt: elapsedTime(now),
      duration: ["error", "reject"].includes(String(type)) ? 1.45 : 1.05,
    };
    if (stillFrame) render(Number(resolved.scene.stillTime) || 0);
  }

  function getStats() {
    return { ...stats };
  }

  function setPaused(nextPaused) {
    if (stillFrame || destroyed) return getStats();
    const paused = Boolean(nextPaused);
    if (paused === manuallyPaused) return getStats();
    const now = typeof performance !== "undefined" ? performance.now() : lastTimestamp;
    if (paused) {
      elapsedOffset = elapsedTime(now);
      manuallyPaused = true;
      startTimestamp = null;
      lastTimestamp = 0;
    } else {
      manuallyPaused = false;
      startTimestamp = now;
      lastTimestamp = 0;
    }
    stats.paused = manuallyPaused;
    if (typeof onStats === "function") {
      try {
        onStats(getStats());
      } catch {
        // Diagnostics must never interrupt the visual loop.
      }
    }
    return getStats();
  }

  function handleVisibility() {
    visible = document.visibilityState !== "hidden";
    if (visible && !manuallyPaused) lastTimestamp = 0;
  }

  function handleReducedMotion(event) {
    const nextReducedMotion = Boolean(event.matches);
    if (nextReducedMotion === reduceMotion) return;
    const now = typeof performance !== "undefined" ? performance.now() : lastTimestamp;
    const continuityTime = elapsedTime(now);
    reduceMotion = nextReducedMotion;
    elapsedOffset = continuityTime;
    startTimestamp = manuallyPaused ? null : now;
    lastTimestamp = 0;
    stats.reducedMotion = reduceMotion;
    qualityScale = qualityScaleFor(resolved.scene, reduceMotion);
    sceneData = makeSceneData(resolved.id, resolved.scene, qualityScale);
    syncEntityStats();
    resize();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (animationFrame && typeof cancelAnimationFrame === "function") cancelAnimationFrame(animationFrame);
    resizeObserver?.disconnect();
    if (typeof window !== "undefined") window.removeEventListener("resize", resize);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", handleVisibility);
    if (mediaQuery) {
      if (typeof mediaQuery.removeEventListener === "function") mediaQuery.removeEventListener("change", handleReducedMotion);
      else if (typeof mediaQuery.removeListener === "function") mediaQuery.removeListener(handleReducedMotion);
    }
    staticCanvas = null;
    staticCtx = null;
  }

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => resize());
    resizeObserver.observe(canvas);
  } else if (typeof window !== "undefined") {
    window.addEventListener("resize", resize, { passive: true });
  }
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", handleVisibility);
  if (mediaQuery) {
    if (typeof mediaQuery.addEventListener === "function") mediaQuery.addEventListener("change", handleReducedMotion);
    else if (typeof mediaQuery.addListener === "function") mediaQuery.addListener(handleReducedMotion);
  }

  syncEntityStats();
  resize();
  if (!stillFrame && typeof requestAnimationFrame === "function") animationFrame = requestAnimationFrame(animate);
  else render(Number(resolved.scene.stillTime) || 0);

  return { setConcept, setPointer, cue, setPaused, resize, destroy, getStats };
}

export default createLivingCompositor;
